import type { TaxTreatment } from './tax';

// Authentication types

export interface CityInfo {
  name: string;
  slug: string;
  count: number;
  /** Representative coordinate (avg of the city's geocoded events); null if none are geocoded. */
  lat?: number | null;
  lng?: number | null;
}

/**
 * Ein Event, reduziert auf das, was ein Pin auf der Umkreis-Karte braucht.
 * Kommt von `GET /events/map-points` — die paginierte Event-Liste reicht für die Karte nicht,
 * weil dort *alle* kommenden Events gleichzeitig sichtbar sind und die Trefferzahl im Radius
 * live mitgerechnet wird.
 */
export interface EventMapPoint {
  id: number;
  title: string;
  lat: number;
  lng: number;
  /**
   * Leitkategorie zuerst, danach die Nebenkategorien — dieselbe Menge, gegen die der
   * Kategoriefilter serverseitig prüft.
   */
  categories: EventCategory[];
  /** ISO-Datum (YYYY-MM-DD). */
  eventDate: string;
  city?: string | null;
  bundesland?: string | null;
  /** Eventart — dieselbe Spalte, gegen die der Eventart-Filter prüft. */
  eventType?: EventType | null;
  /**
   * Ausgeschriebene Streckenlängen in km, aufsteigend und ohne Zeitvarianten — genau die Zeilen,
   * die der Distanzfilter serverseitig prüft. Als Liste, weil der Filter **eine** Strecke
   * verlangt, die beide Grenzen erfüllt (siehe `matchesMapPointFilters`).
   */
  distances?: number[] | null;
  /**
   * Abweichende Eventarten einzelner Strecken. Der Eventart-Filter prüft sie zusätzlich zu
   * `eventType` — serverseitig ein EXISTS über `event_distances.event_type`.
   */
  distanceEventTypes?: EventType[] | null;
  /**
   * Permanentes Event. Kommt nur mit `GET /events/map-points?permanent=true`; `eventDate` ist dann
   * der Platzhalter 1970-01-01 und wird nicht gefiltert.
   */
  permanent?: boolean;
  /** Gebiet statt Ort (permanentes Event ohne Startort) — `lat`/`lng` sind der Mittelpunkt. */
  areaName?: string | null;
  /** Ausdehnung des Gebiets; die Karte zeichnet dann eine Fläche statt eines Pins. */
  areaBounds?: AreaBounds | null;
  /** Permanentes Event in Saisonpause — ausgeblendet, solange `MapPointFilters.includeOffSeason` fehlt. */
  offSeason?: boolean;
}

/** Rechteck eines Gebiets in Grad (WGS84), wie Nominatims `boundingbox`. */
export interface AreaBounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

/** Ein Ort aus dem Ortsverzeichnis — Vorschlag der Umkreis-Suche. */
export interface PlaceSuggestion {
  name: string;
  state: string;
  lat: number;
  lng: number;
}

export function cityToSlug(city: string): string {
  return city
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export enum UserRole {
  USER = 'USER',
  MODERATOR = 'MODERATOR',
  ADMIN = 'ADMIN'
}

export interface User {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  username: string;
  role: UserRole;
  active: boolean;
  location?: string;
  latitude?: number;
  longitude?: number;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string; // letzte Authentifizierung (Login/Refresh); fehlt = nie eingeloggt
  emailVerified?: boolean; // Double-Opt-in bestätigt (Code-/OAuth-Konten: von Anfang an true)
  /**
   * Wie das Konto entstand. null = unbekannt: jedes Konto von vor dem 07.10.2026 und jeder Weg,
   * der (noch) nichts mitschickt. Nur in der Nutzerverwaltung.
   */
  signupSource?: SignupSource | null;
  /** Das Event, auf dessen Seite das Konto entstand. Nur in der Nutzerverwaltung. */
  signupEvent?: { id: number; title: string } | null;
  /** Bei wie vielen Events das Konto „Benachrichtigen“ gedrückt hat. Nur in der Nutzerverwaltung. */
  registrationAlertCount?: number;
  /** Freiwillig (V187): Geburtsjahr. */
  birthYear?: number | null;
  /** Freiwillig (V187): die Radarten, die jemand fährt (`ROAD`, `GRAVEL`, `MTB`). */
  preferredCategories?: string[];
  /**
   * Heimatregion des Kontos (geplant, Winterchallenge). Kanonischer Regionswert wie
   * `CyclingEvent.bundesland`. Nicht dasselbe wie die Region der Regions-Mails: wer sie setzt,
   * bestellt damit keine Mail.
   */
  bundesland?: string | null;
}

/** `PATCH /auth/details`: beide Felder werden immer gesetzt, `null` bzw. leer löscht die Angabe. */
export interface UpdateUserDetailsRequest {
  birthYear: number | null;
  preferredCategories: string[];
}

/**
 * Woher ein Code-Login kommt, mitgeschickt an `/auth/email/verify`. Wirkt nur, wenn dabei ein
 * Konto entsteht (`users.signup_source`, V183). Weitere Wege kommen als weitere Werte dazu,
 * gespiegelt in `SignupOrigin.SOURCES` (Backend, `User.kt`).
 */
export type SignupSource = 'registration-alert' | 'winterchallenge';

export interface SignupOrigin {
  signupSource: SignupSource;
  /** Das Event, auf dessen Seite das Konto entsteht. */
  signupEventId?: number;
}

/**
 * Was ein Code-Login an `/auth/email/verify` mitschicken kann. `digestBundesland` schaltet die
 * Regions-Mails für diese Region ein, bei einem neuen wie bei einem bestehenden Konto: es kommt
 * nur von Wegen, auf denen jemand ausdrücklich die Mails einer Region bestellt.
 */
export interface EmailCodeLoginOptions extends Partial<SignupOrigin> {
  digestBundesland?: string;
}

/** Was Web und App zur Glocke mitschicken, nur für die Zählung (V183). */
export interface RegistrationAlertOrigin {
  /** `account` = war angemeldet, `dialog` = ohne Konto über E-Mail und Einmal-Code. */
  via: 'account' | 'dialog';
}

/**
 * Nutzerzahlen ohne Personenbezug (`GET /api/v1/users/stats`). Moderatoren sehen nur diese
 * Zahlen; die Konten dahinter bleiben Admins vorbehalten. Auch die Adminansicht rechnet damit,
 * damit beide Rollen dieselbe Kennzahl sehen.
 */
export interface UserStats {
  activeWithinDays: number;   // Schwelle „Aktiv" in Tagen seit letztem Login
  dormantWithinDays: number;  // Schwelle „Ruhend"
  confirmed: UserStatsBucket;
  unconfirmed: UserStatsBucket;
  /** Freiwillige Angaben (V187), nur zusammengefasst; gezeichnet über `userDetailsView`. */
  details?: UserDetailsStats | null;
}

/** Gegenstück zu `UserDetailsStats` im Backend (`model/UserStats.kt`). */
export interface UserDetailsStats {
  since: string;          // JJJJ-MM-TT, seit wann gefragt wird
  minGroupSize: number;   // kleinere Gruppen kommen mit count = null
  accounts: number;       // bestätigte, aktive Konten
  withDetails: number;
  withCategories: number;
  withBirthYear: number;
  categories: UserDetailsGroup[];   // ROAD, GRAVEL, MTB
  ageGroups: UserDetailsGroup[];    // U30, 30, 40, 50, 60
}

export interface UserDetailsGroup {
  key: string;
  count: number | null;
}

export interface UserStatsBucket {
  total: number;
  active: number;
  dormant: number;
  inactive: number;   // länger als dormantWithinDays her oder nie eingeloggt
  registrationsPerDay: DailyRegistrationCount[];
}

export interface DailyRegistrationCount {
  day: string;   // JJJJ-MM-TT
  count: number;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  /** Das Konto ist bei diesem Code-Login gerade entstanden: einmal nach den freiwilligen Angaben fragen. */
  newAccount?: boolean;
}

/**
 * Antwort auf die Registrierung mit Passwort. Es gibt bewusst keine Tokens: das Konto ist erst
 * nach dem Klick im Bestätigungslink nutzbar (Double-Opt-in).
 */
export interface RegistrationResult {
  status: 'verification_required';
  email: string;
  verificationSent: boolean;
  message: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  firstName?: string;
  lastName?: string;
  email: string;
  username?: string;
  password: string;
  digestBundesland?: string;
  /** Honigtopf gegen Bots: im Formular unsichtbar, muss leer bleiben. */
  website?: string;
  /** Millisekunden zwischen Formular-Aufbau und Absenden — Bots sind zu schnell. */
  formElapsedMs?: number;
}

export interface CreateUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  username: string;
  password: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  username: string;
  password?: string;
  role: UserRole;
  active: boolean;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface UpdateLocationRequest {
  location: string;
}

// Event types

export enum EventCategory {
  ROAD = 'ROAD',
  GRAVEL = 'GRAVEL',
  MTB = 'MTB'
}

/** Reihenfolge, in der Kategorien überall angezeigt werden (Badges, Auswahl im Formular). */
export const EVENT_CATEGORY_ORDER: EventCategory[] = [
  EventCategory.ROAD,
  EventCategory.GRAVEL,
  EventCategory.MTB,
];

/**
 * Alle Kategorien einer Veranstaltung: Leitkategorie zuerst, danach die zusätzlichen.
 * Ein Volksradfahren kann z. B. gleichzeitig Rennrad, Gravel und MTB sein — Karten, Detailseite
 * und Filter sollen dann alle drei zeigen, ohne dass das Event mehrfach angelegt wird.
 */
export function eventCategories(event: {
  category: EventCategory;
  secondaryCategories?: EventCategory[];
}): EventCategory[] {
  const extra = (event.secondaryCategories ?? []).filter(c => c !== event.category);
  return [event.category, ...EVENT_CATEGORY_ORDER.filter(c => extra.includes(c))];
}

export enum EventType {
  // Legacy-Werte (vor der radsport-events-CSV-Migration) — behalten für Rückwärtskompatibilität.
  RTF = 'RTF',
  BREVET = 'BREVET',
  GRAVEL_RIDE = 'GRAVEL_RIDE',
  MARATHON = 'MARATHON',
  CTF = 'CTF',
  ETAPPE = 'ETAPPE',
  JEDERMANN = 'JEDERMANN',
  TOUR = 'TOUR',
  GROUPRIDE = 'GROUPRIDE',
  SONSTIGE = 'SONSTIGE',

  // Rennrad / ROAD
  BERGZEITFAHREN = 'BERGZEITFAHREN',
  DUATHLON = 'DUATHLON',
  EINZELZEITFAHREN = 'EINZELZEITFAHREN',
  ETAPPENFAHRT = 'ETAPPENFAHRT',
  ETAPPENRENNEN = 'ETAPPENRENNEN',
  HOBBYRENNEN = 'HOBBYRENNEN',
  MANNSCHAFTSZEITFAHREN = 'MANNSCHAFTSZEITFAHREN',
  RADMARATHON = 'RADMARATHON',
  TRIATHLON = 'TRIATHLON',
  RENNEN_24H = 'RENNEN_24H',
  RENNEN_12H = 'RENNEN_12H',
  VINTAGE_TOUR = 'VINTAGE_TOUR',
  VIRTUELLE_RTF = 'VIRTUELLE_RTF',
  VOLKSRADFAHREN = 'VOLKSRADFAHREN',

  // MTB
  RENNEN_2_3_6H = 'RENNEN_2_3_6H',
  DUATHLON_CROSS = 'DUATHLON_CROSS',
  TRIATHLON_CROSS = 'TRIATHLON_CROSS',
  CYCLOCROSS = 'CYCLOCROSS',
  CROSSCOUNTRY = 'CROSSCOUNTRY',
  DOWNHILL = 'DOWNHILL',
  E_MTB = 'E_MTB',
  ENDURO = 'ENDURO',
  ETAPPENTOUR = 'ETAPPENTOUR',
  HILLCLIMB = 'HILLCLIMB',
  MTB_MARATHON = 'MTB_MARATHON',
  MTBO = 'MTBO',
  PUMPTRACKRENNEN = 'PUMPTRACKRENNEN',
  SONSTIGE_MTB = 'SONSTIGE_MTB',

  // Gravel
  GRAVEL_BREVET = 'GRAVEL_BREVET',
  GRAVEL_CAMP = 'GRAVEL_CAMP',
  GRAVEL_RACE = 'GRAVEL_RACE',
  GRAVEL_TOUR = 'GRAVEL_TOUR',
  GRAVEL_SONSTIGES = 'GRAVEL_SONSTIGES',

  // Kategorieübergreifend: Ultra ist ein Format, kein Untergrund — ein Ultra wird auf dem
  // Rennrad (Race Across Germany), auf Gravel (Atlas Mountain Race) oder auf dem MTB gefahren.
  // Deshalb steht ULTRA in EVENT_TYPES_BY_CATEGORY unter allen drei Kategorien.
  // Angezeigt wird die Art als „Ultracycling" (EVENT_TYPE_LABELS); der Schlüssel bleibt ULTRA,
  // weil er in Bestandsdaten und in event_edits steht.
  ULTRA = 'ULTRA',

  // Ebenfalls kategorieübergreifend und bewusst KEIN Kind von ULTRA (s. EVENT_TYPE_CHILDREN):
  // Ultracycling und Bikepacking sind zwei Formate derselben Distanzklasse, nicht Ober- und
  // Unterart. Ultracycling ist das Rennen gegen die Uhr, Bikepacking die überwiegend
  // selbstversorgte Fahrt mit Gepäck (Tour Divide, Silk Road Mountain Race). Wäre Bikepacking
  // ein Kind, brächte jeder Ultracycling-Filter die Bikepacking-Events mit — genau die
  // Vermischung, die die eigene Art auflösen soll.
  BIKEPACKING = 'BIKEPACKING',
}

export enum EventStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED'
}

/**
 * Status-Filter der Redaktionslisten: genau ein Status oder `'ALL'` — online, offline und
 * archiviert in einer Liste. `'ALL'` ist bewusst kein `EventStatus`: es beschreibt eine
 * Sicht auf die Liste, kein Zustand, den ein Event annehmen koennte.
 */
export type EventStatusFilter = EventStatus | 'ALL';

/**
 * Wie man sich zu einer Veranstaltung anmeldet.
 * - `UNKNOWN`: nicht geklärt (so kommt der gesamte Bestand herein)
 * - `ONLINE`: über einen Anmelde-Link (steht als `REGISTRATION` in den Event-Links)
 * - `ONSITE_ONLY`: ausschließlich vor Ort — dann gibt es weder Link noch Anmeldestart
 */
export type RegistrationMode = 'UNKNOWN' | 'ONLINE' | 'ONSITE_ONLY';

export const REGISTRATION_MODE_LABELS: Record<RegistrationMode, string> = {
  UNKNOWN: 'Nicht bekannt',
  ONLINE: 'Online mit Anmelde-Link',
  ONSITE_ONLY: 'Nur vor Ort',
};

/**
 * Der öffentliche Anmeldestand — das Einzige aus dem Anmelde-Block, das Event-Liste und
 * Detailseite zeigen. Bewusst ohne Termin: *dass* die Anmeldung noch nicht offen ist, hilft
 * jedem; *wann genau* sie öffnet, bleibt intern (siehe `registrationStart`).
 *
 * Leitet das Backend ab (`registrationStatusOf`); Clients rechnen nicht selbst.
 */
export type RegistrationStatus =
  | 'UNKNOWN' | 'OPEN' | 'OPENING_SOON' | 'NOT_YET_OPEN' | 'CLOSED' | 'ONSITE_ONLY';

/**
 * Der von Hand gesetzte Stand der Anmeldung — die Alternative zu einem Anmeldestart, wenn man den
 * Termin nicht kennt oder er hinter uns liegt. `CLOSED` heißt: Frist um oder vom Veranstalter
 * beendet — etwas anderes als *ausgebucht* (dort sind die Plätze voll).
 *
 * Ein gesetzter Stand schließt einen Anmeldestart aus; im Formular ist beides dieselbe Auswahl.
 */
export type RegistrationState = 'UNKNOWN' | 'OPEN' | 'CLOSED';

/**
 * Woran die Redaktion erkennt, wen sie zuerst anschreibt. Keine Punktzahl: dieselbe Zeile ist
 * für verschiedene Angebote verschieden gut (Event in einem Tag: für eine Hervorhebung zu spät,
 * für den Zugang erste Wahl). Die Empfehlung ist eine benannte Absicht mit Regeln in der Liste.
 */
export interface OrganizerOutreachSignals {
  /** Personen, die in den letzten 30 Tagen ein Event dieses Veranstalters angesehen haben. */
  visitors30d: number;
  views30d: number;
  /** Von seinen Event-Seiten auf seinen Link geklickt, letzte 30 Tage. */
  clicks30d: number;
  /** Veröffentlichte Events ab heute. */
  upcomingEvents: number;
  nextEventDate?: string;
  nextEventInDays?: number;
  /** Das jüngste vergangene Event, wenn keins ansteht: „steht euer Termin schon fest?" */
  lastEventDate?: string;
  /** Ein kommendes Event hat die Anmeldung offen oder einen künftigen Anmeldetermin. */
  registrationOpen: boolean;
  /** Bestätigte Konten, deren Mail-Domain passt und die noch nicht verknüpft sind. */
  matchingAccounts: number;
}

export interface Organizer {
  id: number;
  name: string;
  orgKind?: OrganizerKind;
  /** Änderungen dieses Veranstalters gehen ohne manuelle Prüfung live. */
  autoApproveEdits?: boolean;
  /**
   * Der Haken: ein bestätigtes Konto pflegt dieses Profil. Kostenlos — er sagt etwas über
   * Identität, nicht über ein gebuchtes Paket (siehe `verification`).
   */
  verified?: boolean;
  /** Die Premium-Buchung. Admins sehen auch geplante und abgelaufene. */
  verification?: OrganizerVerification;
  contactPerson?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  bundesland?: string;
  bdrClubNr?: string;
  radNetUrl?: string;
  impressumUrl?: string;
  logoUrl?: string;
  photoUrl?: string;
  stravaClubUrl?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  komootUrl?: string;
  /**
   * **Wer** ihn pflegt — die Kontonamen der aktiven Verknüpfungen.
   *
   * Nur in der Redaktionsliste (`GET /api/v1/organizers/admin`) gefüllt und bewusst nicht in
   * `PublicOrganizer`: dass ein Profil betreut wird, ist eine öffentliche Aussage (der Haken),
   * von wem, nicht.
   */
  managers?: string[];
  /**
   * Seit wann ein Konto ihn pflegt: der früheste noch aktive Zugang. Wie `managers` nur in der
   * Redaktionsliste; daran sieht sie, welcher Veranstalter zuletzt dazugekommen ist
   * (`istNeuVerifiziert`).
   */
  verifiedSince?: string;
  /**
   * Wann diesem Veranstalter zuletzt eine Outreach-Mail **zugestellt** wurde — leer heißt: nie.
   *
   * Wie `managers` nur in der Redaktionsliste. Sie trägt die Spalte „Angeschrieben“, an der sich
   * eine Liste mit hunderten Vereinen der Reihe nach abarbeiten lässt.
   */
  lastOutreachAt?: string;
  /**
   * Wen zuerst anschreiben: die Merkmale, aus denen die Liste ihre Empfehlung je Ziel baut.
   * Nur in der Redaktionsliste, gerechnet im Backend (`OutreachSignalService`).
   */
  outreachSignals?: OrganizerOutreachSignals;
  /** Was von den Stammdaten öffentlich steht — die Entscheidung des Veranstalters. */
  visibility?: OrganizerVisibility;
  /** Logo bzw. Banner liegen als hochgeladene Datei vor (dann lässt sich „Entfernen" anbieten). */
  logoUploaded?: boolean;
  bannerUploaded?: boolean;
  /** Ein hochgeladenes Bild wartet auf Freigabe durch Redaktion oder Admin. */
  logoPending?: boolean;
  bannerPending?: boolean;
  active: boolean;
  eventCount: number;
}

/**
 * Welche Stammdaten öffentlich stehen.
 *
 * Voreinstellung bildet die früher fest verdrahtete Aufteilung ab: Anschrift, Website und
 * Social-Media ja, Ansprechpartner/E-Mail/Telefon nein. Entschieden wird das im
 * Veranstalter-Bereich, nicht von der Redaktion.
 */
export interface OrganizerVisibility {
  publicAddress: boolean;
  publicWebsite: boolean;
  publicSocial: boolean;
  publicContactPerson: boolean;
  publicEmail: boolean;
  publicPhone: boolean;
}

export const DEFAULT_ORGANIZER_VISIBILITY: OrganizerVisibility = {
  publicAddress: true,
  publicWebsite: true,
  publicSocial: true,
  publicContactPerson: false,
  publicEmail: false,
  publicPhone: false,
};

export interface PublicOrganizer {
  id: number;
  name: string;
  /**
   * Der Haken. Steht, sobald ein bestätigtes Konto dieses Profil pflegt — kostenlos und
   * unabhängig von Premium. Er beeinflusst weder Reihenfolge noch Filter.
   */
  verified?: boolean;
  /**
   * Läuft gerade Premium? Erklärt nach außen nur, warum Banner und Bilder stehen dürfen.
   *
   * Bewusst kein Zeitraum: seit wann und wie lange ein Verein zahlt, geht Besucher nichts an.
   * Die vollständige Buchung trägt `Organizer.verification` — Redaktion und Verein selbst.
   */
  premium?: boolean;
  /** Kommende sichtbare Events, nur in der Verzeichnisliste (`GET /organizers`, 30.10.2026). */
  upcomingEventCount?: number;
  website?: string;
  address?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  bundesland?: string;
  logoUrl?: string;
  photoUrl?: string;
  stravaClubUrl?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  komootUrl?: string;
  /** Nur gesetzt, wenn der Veranstalter sie freigegeben hat. */
  contactPerson?: string;
  email?: string;
  phone?: string;
  active: boolean;
}

export interface CreateOrganizerRequest {
  name: string;
  /**
   * Verein oder kommerzieller Anbieter — entscheidet über den Preis.
   *
   * Weglassen heißt „unverändert lassen", **nicht** „unbekannt": sonst setzte jedes Formular,
   * das das Feld nicht kennt, die Preisgruppe eines zahlenden Kunden still zurück.
   */
  orgKind?: OrganizerKind;
  contactPerson?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  bundesland?: string;
  radNetUrl?: string;
  impressumUrl?: string;
  logoUrl?: string;
  photoUrl?: string;
  stravaClubUrl?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  komootUrl?: string;
}

export interface UpdateOrganizerRequest extends CreateOrganizerRequest {
  active: boolean;
}

export enum OrganizerEditStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface OrganizerEditResponse {
  id: number;
  organizerId?: number;
  organizerName?: string;
  submittedBy?: number;
  /** Gesetzt, wenn die Einreichung vom verknuepften Veranstalter kam. */
  submittedAsOrganizerId?: number;
  /**
   * „Veranstalter eintragen": der Einreichende will den neuen Eintrag danach verwalten. Die
   * Redaktion prüft Veranstalter und Zugang in einem Schritt; beim Freigeben entsteht beides.
   */
  claimAccess?: boolean;
  status: OrganizerEditStatus;
  proposedName?: string;
  proposedWebsite?: string;
  proposedLogoUrl?: string;
  proposedStreet?: string;
  proposedPostalCode?: string;
  proposedCity?: string;
  proposedBundesland?: string;
  proposedImpressumUrl?: string;
  proposedContactPerson?: string;
  proposedEmail?: string;
  proposedPhone?: string;
  proposedStravaClubUrl?: string;
  proposedInstagramUrl?: string;
  proposedFacebookUrl?: string;
  proposedKomootUrl?: string;
  submitterName?: string;
  submitterEmail?: string;
  note?: string;
  reviewerComment?: string;
  reviewedAt?: string;
  createdAt?: string;
  currentOrganizer?: Organizer;
}

export interface ProposeOrganizerEditRequest {
  proposedName?: string;
  proposedWebsite?: string;
  proposedLogoUrl?: string;
  proposedStreet?: string;
  proposedPostalCode?: string;
  proposedCity?: string;
  proposedBundesland?: string;
  proposedImpressumUrl?: string;
  proposedContactPerson?: string;
  proposedEmail?: string;
  proposedPhone?: string;
  proposedStravaClubUrl?: string;
  proposedInstagramUrl?: string;
  proposedFacebookUrl?: string;
  proposedKomootUrl?: string;
  submitterName?: string;
  submitterEmail?: string;
  note?: string;
}

export interface ReviewOrganizerEditRequest {
  approved: boolean;
  comment?: string;
}

/**
 * Zustand einer Verknuepfung zwischen Konto und Veranstalter.
 *
 * Selbstantrag und erteilter Zugang sind derselbe Datensatz: ein Antrag von der oeffentlichen
 * Veranstalter-Seite beginnt als REQUESTED, eine Verknuepfung durch die Redaktion direkt als
 * ACTIVE. Nur ACTIVE gibt Rechte.
 */
export enum OrganizerMemberStatus {
  REQUESTED = 'REQUESTED',
  ACTIVE = 'ACTIVE',
  REJECTED = 'REJECTED',
  REVOKED = 'REVOKED',
}

/**
 * „Veranstalter eintragen" — für Vereine und Anbieter, die im Verzeichnis fehlen. Die wenigen
 * Angaben, die ein Vorstand aus dem Kopf weiß; alles Weitere pflegt er danach im Briefkopf.
 */
export interface RegisterOrganizerRequest {
  name: string;
  website?: string;
  email?: string;
  postalCode?: string;
  city?: string;
  note?: string;
  /** Ohne Konto: wer den Vorschlag gemacht hat, damit die Redaktion nachfragen kann. */
  submitterName?: string;
  submitterEmail?: string;
}

/**
 * Drei Ausgänge, ein Typ: steht schon da (`existingOrganizerId`, dann Antrag dort), sofort
 * angelegt und verknüpft (`organizerId`, die Adresse passte) oder als Vorschlag in der Prüfung
 * (`editId`).
 */
export interface RegisterOrganizerResponse {
  existingOrganizerId?: number;
  existingOrganizerName?: string;
  organizerId?: number;
  editId?: number;
}

export interface OrganizerMember {
  id: number;
  organizerId: number;
  /**
   * Nur in der Antwort auf einen frischen Antrag: die maskierte Adresse des Veranstalters, an die
   * gerade ein Freischalt-Link gegangen ist (V160). `undefined` heißt, dass keiner rausging — der
   * Veranstalter hat keine Adresse bei uns, oder die Tagesgrenze war erreicht.
   */
  askedEmail?: string;
  organizerName?: string;
  userId: number;
  username?: string;
  userEmail?: string;
  /**
   * Belege für die Prüfung eines Selbstantrags. `emailDomainMatches` ist der stärkste Hinweis,
   * den wir automatisch haben — eine Adresse auf der Domain der Vereinsseite oder der
   * hinterlegten Vereinsadresse bekommt praktisch nur, wer dort mitarbeitet. `undefined` heißt
   * „nicht prüfbar" (weder Website noch Vereinsadresse, oder keine Adresse am Konto) und ist
   * etwas anderes als „passt nicht".
   *
   * Er trägt nur, solange die Adresse **bestätigt** ist: eine unbestätigte kann sich jeder
   * eintragen. Beim Wechsel im Profil fällt die Bestätigung zurück. Trifft beides zu, gibt der
   * Server den Zugang sofort frei; solche Anträge stehen nie in der Liste der offenen.
   */
  userEmailVerified?: boolean;
  userSince?: string;
  organizerWebsite?: string;
  emailDomainMatches?: boolean;
  status: OrganizerMemberStatus;
  requestNote?: string;
  decisionComment?: string;
  decidedAt?: string;
  createdAt?: string;
  /**
   * Der zuletzt verschickte Freischalt-Link an die Adresse des Veranstalters (V160), ungekürzt.
   * Nur in den Listen der Redaktion; fehlt, wenn keiner rausging. Ein geklickter Link macht den
   * Antrag sofort zum Zugang — steht hier einer, ist er also noch nicht benutzt.
   */
  approvalLinkSentTo?: string;
  approvalLinkSentAt?: string;
  approvalLinkExpiresAt?: string;
}

export interface RequestOrganizerAccessRequest {
  organizerId: number;
  note?: string;
}

export interface DecideOrganizerAccessRequest {
  approved: boolean;
  comment?: string;
}

export interface GrantOrganizerAccessRequest {
  organizerId: number;
  userId: number;
}

/**
 * Was gebucht werden kann.
 *
 * `VERIFICATION` heißt im Verkauf **Premium** — der Schlüssel bleibt, weil er in `price_items`,
 * `booking_requests` und den Spalten `verified_*` steht und eine Umbenennung nichts besser
 * machte. Der Haken, nach dem der Schlüssel benannt ist, gehört seit dem 25.08.2026 nicht mehr
 * dazu; er ist kostenlos.
 */
export type BookingKind = 'VERIFICATION' | 'PROMOTION' | 'SOCIAL_POST' | 'NEWS_ARTICLE';

export const BOOKING_KIND_LABELS: Record<BookingKind, string> = {
  VERIFICATION: 'Premium',
  PROMOTION: 'Hervorhebung',
  SOCIAL_POST: 'Social-Media-Beitrag',
  NEWS_ARTICLE: 'News-Beitrag',
};

/** Preisgruppe. Vereine zahlen weniger als kommerzielle Anbieter. */
export type PriceAudience = 'CLUB' | 'COMMERCIAL' | 'ANY';

/**
 * Zustand der Premium-Buchung aus Sicht des Veranstalters.
 *
 * Steht hier und nicht je Plattform, weil Web und App dieselbe Frage beantworten muessen: laeuft
 * es noch, und wie lange? Zwei Rechenwege heisst frueher oder spaeter zwei verschiedene Restlaufzeiten
 * in derselben Woche.
 */
export type PremiumStateKind = 'active' | 'expiring' | 'expired' | 'planned';

export interface PremiumState {
  kind: PremiumStateKind;
  /** Tage bis zum Ende (`active`/`expiring`), bis zum Beginn (`planned`) bzw. seit dem Ende. */
  days: number;
}

/**
 * Ab hier ist die Restlaufzeit eine Nachricht, keine Nebenbemerkung.
 *
 * Es gibt keinen Zahlungsanbieter und damit keine automatische Verlaengerung: laeuft Premium aus,
 * verschwinden Logo, Banner, Zahlen und Benachrichtigungen. Ein Monat ist die Zeit, die ein Verein
 * fuer eine Rechnung durch seinen Vorstand braucht.
 */
export const PREMIUM_WARN_DAYS = 30;

/**
 * Wie lange ein Ablauf noch der Rede wert ist.
 *
 * Danach ist „abgelaufen" keine Nachricht mehr, sondern eine dauerhafte Beschriftung — dieselbe
 * halbjaehrige Frist, mit der die Redaktionsliste (`/organizer-access/premium`) rechnet.
 */
export const PREMIUM_EXPIRED_GRACE_DAYS = 180;

/** `null` heisst: nie etwas gebucht (oder laengst vorbei) — dann sagt die Anzeige gar nichts. */
export function premiumState(
  v?: OrganizerVerification | null,
  now: number = Date.now(),
): PremiumState | null {
  if (!v) return null;
  const day = 86400000;
  const until = new Date(v.until).getTime();
  const from = new Date(v.from).getTime();
  if (v.active) {
    const days = Math.ceil((until - now) / day);
    return { kind: days <= PREMIUM_WARN_DAYS ? 'expiring' : 'active', days: Math.max(0, days) };
  }
  if (now < from) return { kind: 'planned', days: Math.ceil((from - now) / day) };
  const since = Math.max(0, Math.ceil((now - until) / day));
  return since > PREMIUM_EXPIRED_GRACE_DAYS ? null : { kind: 'expired', days: since };
}

export const PRICE_AUDIENCE_LABELS: Record<PriceAudience, string> = {
  CLUB: 'Vereine',
  COMMERCIAL: 'Kommerzielle Anbieter',
  ANY: 'Alle',
};

/** Einordnung eines Veranstalters — bestimmt, welcher Preis fuer ihn gilt. */
export type OrganizerKind = 'CLUB' | 'COMMERCIAL' | 'UNKNOWN';

export const ORGANIZER_KIND_LABELS: Record<OrganizerKind, string> = {
  CLUB: 'Verein',
  COMMERCIAL: 'Kommerzieller Anbieter',
  UNKNOWN: 'Nicht eingeordnet',
};

/** Laufzeit einer Leistung. */
export type PriceVariant = 'DAYS_7' | 'MONTH_1' | 'UNTIL_EVENT' | 'YEAR_1';

export const PRICE_VARIANT_LABELS: Record<PriceVariant, string> = {
  DAYS_7: '7 Tage',
  MONTH_1: '1 Monat',
  UNTIL_EVENT: 'Bis zur Veranstaltung',
  YEAR_1: '1 Jahr',
};

export type BookingStatus = 'OPEN' | 'APPLIED' | 'DECLINED' | 'CANCELLED';

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  OPEN: 'Zahlung ausstehend',
  APPLIED: 'Freigeschaltet',
  DECLINED: 'Abgelehnt',
  CANCELLED: 'Zurückgezogen',
};

/**
 * Woran eine Bestellung steht — die Auskunft, die man tatsächlich sucht.
 *
 * Der gespeicherte `BookingStatus` sagt nur, ob die Leistung gesetzt wurde; was dazwischen
 * passiert (Rechnung raus, Geld da), rechnet der Server aus Rechnungsnummer und Zahlungseingang
 * aus. Ein „In Bearbeitung" gibt es nicht (bis V166 gab es den Status `IN_PROGRESS`): der
 * Zustand sagte nie etwas über den Vorgang, nur darüber, dass jemand hingeschaut hat.
 */
export type BookingStage =
  | 'AWAITING_PAYMENT'
  | 'INVOICE_SENT'
  | 'PAID'
  | 'ACTIVATED'
  | 'DECLINED'
  | 'CANCELLED';

export const BOOKING_STAGE_LABELS: Record<BookingStage, string> = {
  AWAITING_PAYMENT: 'Zahlung ausstehend',
  INVOICE_SENT: 'Rechnung verschickt',
  PAID: 'Zahlung erhalten',
  ACTIVATED: 'Freigeschaltet',
  DECLINED: 'Abgelehnt',
  CANCELLED: 'Zurückgezogen',
};

export interface PriceItem {
  id: number;
  kind: BookingKind;
  audience: PriceAudience;
  variant?: PriceVariant;
  /** Nur bei `PROMOTION`: welche Card-Stufe dieser Preis meint. Jede Stufe kostet eigenes Geld. */
  promoTier?: PromotionTier;
  label: string;
  description?: string;
  amountCents: number;
  vatPercent: number;
  /**
   * Ob amountCents brutto gemeint ist. **Seit V159 ist der Normalfall netto**: die Steuer kommt
   * oben drauf. Brutto steht noch an eingefrorenen Buchungen aus der Zeit davor.
   */
  vatIncluded: boolean;
  sortOrder: number;
  active: boolean;
}

export interface PriceItemRequest {
  kind: BookingKind;
  audience: PriceAudience;
  variant?: PriceVariant | null;
  promoTier?: PromotionTier | null;
  label: string;
  description?: string;
  amountCents: number;
  vatPercent: number;
  vatIncluded: boolean;
  sortOrder: number;
  active: boolean;
}

/**
 * Eine buchbare Leistung samt Preis.
 *
 * `available: false` heisst: die Laufzeit passt nicht mehr in die Zeit bis zum Event. Das Angebot
 * bleibt sichtbar, damit klar ist, dass es die Option gibt — buchbar ist sie nicht.
 */
export interface BookingOption {
  priceItemId: number;
  kind: BookingKind;
  /**
   * Für welche Preisgruppe dieses Angebot gilt.
   *
   * Ein nicht eingeordneter Veranstalter sieht beide Premium-Preise; die Auswahl muss sagen,
   * welcher welcher ist. Aus dem Betrag zu raten wäre der falsche Weg.
   */
  audience: PriceAudience;
  variant?: PriceVariant;
  /** Bei einer Hervorhebung: welche Card-Stufe dieses Angebot bringt. */
  promoTier?: PromotionTier;
  label: string;
  description?: string;
  amountCents: number;
  vatPercent: number;
  vatIncluded: boolean;
  netCents: number;
  grossCents: number;
  /**
   * Rabattsatz, der in den Beträgen **bereits steckt** (0 = keiner).
   *
   * Die Betragsfelder tragen immer den zu zahlenden Preis — es wird nirgends im Client
   * nachgerechnet, ein Rabatt ist eine Preisentscheidung des Servers. `listNetCents` ist der
   * durchgestrichene Preis daneben, weil das Angebot in Nettopreisen rechnet.
   */
  discountPercent: number;
  listNetCents?: number;
  listGrossCents?: number;
  available: boolean;
  unavailableReason?: string;
  /** Wie viele Tage diese Laufzeit bei diesem Event tatsächlich abdeckt. */
  coverageDays?: number;
  /** Die Laufzeit reicht bis zur Veranstaltung — dann heißt die Leistung für den Kunden genau das. */
  runsUntilEvent?: boolean;
}

/** Unsere Kontodaten, wie sie beim Überweisen gebraucht werden. */
export interface BankAccount {
  accountHolder: string;
  /**
   * Anschrift des Empfängers, einzeilig. Banken außerhalb des SEPA-Inlands verlangen sie —
   * AUDAX Suisse konnte ohne sie nicht überweisen (24.10.2026).
   */
  accountHolderAddress?: string;
  iban: string;
  bic?: string;
  bank?: string;
}

export interface BookingRequestResponse {
  id: number;
  organizerId: number;
  organizerName?: string;
  requestedByUsername?: string;
  kind: BookingKind;
  variant?: PriceVariant;
  /** Die bestellte Card-Stufe — „übernehmen" setzt genau sie. */
  promoTier?: PromotionTier;
  eventId?: number;
  eventTitle?: string;
  eventDate?: string;
  /** Gesetzt, solange das Event selbst noch als Vorschlag in der Prüfung liegt. */
  eventEditId?: number;
  amountCents: number;
  vatPercent: number;
  vatIncluded: boolean;
  /** Rabattsatz, der im eingefrorenen Betrag steckt (0 = keiner). */
  discountPercent: number;
  netCents: number;
  grossCents: number;
  priceLabel: string;
  /** Was auf die Rechnung gehört — vom Kunden bei der Bestellung angegeben und eingefroren. */
  billingName?: string;
  billingAddress?: string;
  /** Land des Rechnungsempfängers (ISO-Code), eingefroren. Fehlt bei Bestellungen vor V190. */
  billingCountry?: string;
  /** USt-IdNr. des Kunden — nur bei EU-Kunden außerhalb Deutschlands. */
  vatId?: string;
  /**
   * Steuerfall der Bestellung (`shared/src/tax.ts`), beim Anlegen vom Server festgestellt und
   * eingefroren wie der Steuersatz. Bei `NON_EU` und `EU_REVERSE_CHARGE` ist `vatPercent` 0.
   */
  taxTreatment?: TaxTreatment;
  /**
   * Verwendungszweck der Überweisung. Damit findet die Redaktion die Zahlung im Kontoauszug
   * wieder — der Verkauf läuft auf Vorkasse, die Rechnung folgt nach dem Geldeingang.
   */
  paymentReference: string;
  /**
   * Wohin das Geld soll — nur solange die Anfrage offen ist.
   *
   * Damit die Oberfläche die Kontodaten *zeigen* kann, statt sie zu beschreiben. Fehlt, sobald
   * bezahlt/abgelehnt/zurückgezogen — eine Kontonummer ohne offenen Betrag ist nur Rauschen.
   */
  bank?: BankAccount;
  proposedContent?: string;
  note?: string;
  status: BookingStatus;
  handledComment?: string;
  handledAt?: string;
  createdAt?: string;
  /** Bis wann der Betrag da sein soll, als Kalendertag `YYYY-MM-DD` — nur solange die Anfrage offen ist. */
  paymentDueAt?: string;
  /**
   * Ob sich eine Rechnung vorab anfordern lässt.
   *
   * Der Regelfall bleibt Vorkasse. Wer eine Buchhaltung hat, die ohne Beleg nicht überweist,
   * holt die Rechnung hiermit vor — freigeschaltet wird trotzdem erst nach dem Zahlungseingang.
   * `false`, sobald eine Rechnung vorliegt: eine zweite zur selben Bestellung wäre ein
   * doppelter Beleg.
   */
  canRequestInvoice?: boolean;
  /** Gesetzt, sobald der Kunde die Rechnung angefordert hat. Damit ist die Bestellung verbindlich. */
  invoiceRequestedAt?: string;
  /**
   * Ob sich die Bestellung noch selbst zurückziehen lässt.
   *
   * Nach einer gestellten Rechnung nicht mehr: sie ist festgeschrieben und nur per
   * Stornorechnung aufzuheben — das gehört vor einen Menschen.
   */
  canCancel?: boolean;
  /** Woran die Bestellung steht — aus Status, Rechnung und Zahlungseingang errechnet. */
  stage?: BookingStage;
  /** Wann die Zahlung eingegangen ist. */
  paidAt?: string;
  /** Die Rechnungsnummer, sobald sie vorliegt — sie ist dann auch der `paymentReference`. */
  invoiceNumber?: string;
  /**
   * Warum die Rechnung nicht entstanden ist — für die Redaktionsansicht.
   *
   * Dem Veranstalter wird sie nicht gezeigt; ihm genügt „ist angefordert".
   */
  invoiceError?: string;
  /** Bezahlt, aber ohne Rechnung — die Redaktion kann sie nachholen. */
  invoiceMissing?: boolean;
  /** Veranstalter weder als Verein noch als kommerziell eingeordnet: ohne das keine Rechnung. */
  organizerUnclassified?: boolean;
  /** Für wen der bestellte Preis galt — Vorschlag für die Einordnung. */
  bookedAudience?: PriceAudience;
  /**
   * Zahlungsziel verstrichen, noch kein Geld da — der Chip „überfällig" in der Redaktionsansicht.
   * Rechnet der Server, damit Chip, Erinnerung und Stornierung denselben Tag meinen.
   */
  overdue?: boolean;
  /** Wann der Kunde an die Zahlung erinnert wurde (einmal, vom täglichen Lauf). */
  paymentReminderSentAt?: string;
  /**
   * Tag (`YYYY-MM-DD`), an dem der tägliche Lauf die Bestellung storniert, wenn bis dahin nichts
   * eingeht. Fehlt, solange nicht erinnert wurde, und bei angeforderter Rechnung — die storniert
   * nur ein Mensch.
   */
  autoCancelOn?: string;
  /** Ob „übernehmen" möglich ist — bei einem noch ungeprüften Event nicht. */
  applicable: boolean;
}

/**
 * Die Zeile zur Zahlungsfrist in der Redaktionsansicht, oder `null`, solange nichts überfällig ist.
 *
 * Sie beantwortet die Frage, die der Chip aufwirft: seit wann, ist der Kunde schon erinnert, und
 * was passiert als Nächstes von allein? Ohne den letzten Teil storniert der Lauf eine Bestellung,
 * von der die Redaktion am Vortag nicht wusste, dass es so weit ist.
 */
export function paymentDeadlineNote(
  booking: Pick<BookingRequestResponse, 'overdue' | 'paymentDueAt' | 'paymentReminderSentAt' | 'autoCancelOn'>,
): string | null {
  if (!booking.overdue) return null;
  const day = (iso: string) => {
    // `YYYY-MM-DD` ohne Uhrzeit als Kalendertag lesen, nicht als UTC-Mitternacht.
    const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + 'T12:00:00Z') : new Date(iso);
    return d.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });
  };
  const parts: string[] = [];
  if (booking.paymentDueAt) parts.push('Zahlungsziel ' + day(booking.paymentDueAt));
  parts.push(booking.paymentReminderSentAt
    ? 'erinnert am ' + day(booking.paymentReminderSentAt)
    : 'noch nicht erinnert');
  if (booking.autoCancelOn) {
    parts.push('wird am ' + day(booking.autoCancelOn) + ' automatisch storniert');
  } else if (booking.paymentReminderSentAt) {
    parts.push('Stornierung nur von Hand');
  }
  return parts.join(' · ');
}

export interface CreateBookingRequest {
  /** Genau der Preis, den der Dialog gezeigt hat — nötig, wenn zwei zur Auswahl standen. */
  priceItemId?: number;
  kind: BookingKind;
  /**
   * Rechnungsempfänger und -anschrift. **Pflicht.**
   *
   * Ohne sie lässt sich nach dem Zahlungseingang keine Rechnung schreiben, und danach antwortet
   * erfahrungsgemäß niemand mehr. Bewusst nicht aus den Stammdaten abgeleitet: die Anschrift dort
   * stammt größtenteils aus Importen und dem Adress-Enricher, ist also nicht vom Kunden bestätigt.
   * Der Dialog füllt sie damit nur *vor*.
   */
  billingName?: string;
  billingAddress?: string;
  /**
   * Land des Rechnungsempfängers (ISO-Code). Entscheidet über die MwSt. (`taxTreatmentOf`);
   * fehlt es, nimmt der Server das Land aus der Region des Veranstalters.
   */
  billingCountry?: string;
  /** USt-IdNr. — nur bei EU-Kunden außerhalb Deutschlands, macht die Bestellung steuerfrei. */
  vatId?: string;
  variant?: PriceVariant;
  /** Bei einer Hervorhebung Pflicht: welche der drei Stufen gebucht wird. */
  promoTier?: PromotionTier;
  eventId?: number;
  eventEditId?: number;
  proposedContent?: string;
  note?: string;
}

/**
 * Eine laufende, geplante oder gerade abgelaufene Hervorhebung — der **Zustand** je Event.
 *
 * Die Buchungsliste zeigt Vorgänge, diese Liste zeigt, was gerade läuft und wie lange noch. Gerechnet wird im Backend (`daysLeft`), das Frontend
 * formatiert nur.
 */
export interface EventPromotionRow {
  eventId: number;
  title: string;
  eventDate: string;
  organizerId?: number;
  organizerName?: string;
  tier: PromotionTier;
  label: string;
  from: string;
  until: string;
  active: boolean;
  /** Negativ, wenn die Buchung abgelaufen ist. */
  daysLeft: number;
  /** Die Buchung reicht bis zur Veranstaltung — dann ist Verlängern gekauftes Nichts. */
  coversEvent: boolean;
}

/**
 * Die Premium-Buchung eines Veranstalters.
 *
 * Hieß einmal „Verifizierung", daher der Name im Backend (`verified_*`). Der **Haken** hängt
 * seit der Premium-Runde nicht mehr daran, sondern an `PublicOrganizer.verified`.
 */
export interface OrganizerVerification {
  tier: PriceAudience;
  from: string;
  until: string;
  /** Läuft gerade. Öffentliche Antworten liefern nur aktive Verifizierungen. */
  active: boolean;
}

/**
 * Brutto- und Nettobetrag eines Preiseintrags in Cent.
 *
 * Seit V159 sind Preise **netto** hinterlegt (`vatIncluded = false`), vorher waren sie brutto;
 * eingefrorene Buchungen aus dieser Zeit tragen es weiter. Beide Richtungen stehen deshalb hier
 * und nicht in jeder Oberfläche einzeln — zwei Rechenwege ergeben irgendwann zwei Preise für
 * dieselbe Sache.
 *
 * **Angeboten wird netto, überwiesen wird brutto.** Wer einen Preis zeigt, nimmt `netCentsOf`
 * und schreibt `vatNote()` daneben; wer einen zu zahlenden Betrag zeigt (Bestätigung, Mail,
 * Rechnung, Kontoabgleich), nimmt `grossCentsOf`.
 */
export const grossCentsOf = (item: { amountCents: number; vatPercent: number; vatIncluded: boolean }): number =>
  item.vatIncluded ? item.amountCents : Math.round(item.amountCents * (100 + item.vatPercent) / 100);

export const netCentsOf = (item: { amountCents: number; vatPercent: number; vatIncluded: boolean }): number =>
  item.vatIncluded ? Math.round(item.amountCents * 100 / (100 + item.vatPercent)) : item.amountCents;

/**
 * IBAN in Vierergruppen: "DE02120300000000202051" → "DE02 1203 0000 0000 2020 51".
 *
 * So steht sie auf jedem Kontoauszug und in jeder Banking-App — ungruppiert vertippt man sich
 * beim Abschreiben.
 */
export const formatIban = (iban: string): string =>
  (iban || '').replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();

/** Cent als deutscher Betrag: 1234 → "12,34 €". */
export const formatEuro = (cents: number): string =>
  `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/**
 * Das Angebot der Hervorhebung, wie der Checkout es zeigt: **ein** Grundpreis (Rahmen, bis zur
 * Veranstaltung) und die dunkle Card als Häkchen mit ihrem Aufpreis.
 *
 * Gerechnet aus den Optionen des Servers, damit Web und App dieselbe Auswahl treffen. Der Server
 * bleibt die Grenze — er kennt Stufe, Laufzeit und Preis-Id der Anfrage und prüft sie. Grundlage
 * ist die günstigste buchbare Option je Stufe, die bis zur Veranstaltung reicht; fällt sie aus
 * (Event vorbei, Vorschlag ohne Datum), steht ihr Grund in `reason`.
 *
 * `upsellExtraCents` ist die Differenz der **Nettopreise** — bei Premium-Rabatt also die
 * rabattierte. Netto, weil der Aufpreis neben den Nettopreisen des Checkouts steht (V159); der
 * Bruttobetrag entsteht erst aus der Summe, und zwar im Backend.
 * Fehlt die dunkle Card im Angebot (abgeschaltet oder gerade nicht buchbar), gibt es kein Häkchen.
 */
export interface PromotionOffer {
  base?: BookingOption;
  upsell?: BookingOption;
  upsellExtraCents?: number;
  /** Warum gerade nichts zu buchen ist — der Grund der Grundoption oder „nicht im Angebot". */
  reason?: string;
}

export function promotionOffer(
  options: BookingOption[],
  baseTier: PromotionTier = 'FRAME',
  upsellTier: PromotionTier = 'INVERTED',
): PromotionOffer {
  const promos = options.filter(o => o.kind === 'PROMOTION');
  const pick = (tier: PromotionTier): BookingOption | undefined => {
    const ofTier = promos.filter(o => o.promoTier === tier);
    const available = ofTier.filter(o => o.available);
    const untilEvent = available.filter(o => o.runsUntilEvent);
    const pool = untilEvent.length > 0 ? untilEvent : available;
    if (pool.length > 0) return pool.slice().sort((a, b) => a.netCents - b.netCents)[0];
    return ofTier[0];
  };
  const base = pick(baseTier);
  if (!base) return { reason: 'Die Hervorhebung ist derzeit nicht buchbar.' };
  if (!base.available) return { base, reason: base.unavailableReason };
  const upsell = pick(upsellTier);
  if (!upsell || !upsell.available || upsell.netCents <= base.netCents) return { base };
  return { base, upsell, upsellExtraCents: upsell.netCents - base.netCents };
}

/** Ein Veranstalter, den das angemeldete Konto pflegen darf. */
export interface OrganizerPortalSummary {
  id: number;
  name: string;
  eventCount: number;
  upcomingEventCount: number;
  /** Eigene Vorschlaege, die noch in der Pruefung liegen — Events und Stammdaten zusammen. */
  pendingEditCount: number;
  /**
   * Änderungen dieses Veranstalters gehen ohne manuelle Prüfung live. Steuert die Wortwahl:
   * „ist online" statt „wird geprüft".
   */
  autoApproveEdits: boolean;
  /**
   * Premium läuft — schaltet Banner, die Zahlen und die Benachrichtigungen an Teilnehmer frei.
   * Bild und Flyer am Event sind seit dem 11.09.2026 kostenlos.
   *
   * Der Haken steht hier bewusst nicht: wer diese Zusammenfassung bekommt, ist per Definition
   * aktiver Verwalter und trägt ihn deshalb immer.
   */
  premium: boolean;
}

/**
 * Ein Event aus Sicht seines Veranstalters.
 *
 * Bewusst nicht der volle Admin-Blick: Herkunfts-URL, externe Id und Anreicherungs-Score fehlen,
 * interne Links ebenso. Status und Anmeldefelder sind dagegen dabei — die braucht er.
 */
export interface OrganizerPortalEvent {
  event: CyclingEvent;
  registrationStart?: string;
  registrationMode: RegistrationMode;
  registrationState: RegistrationState;
  /** Offener eigener Vorschlag zu diesem Event — dann ist Bearbeiten gesperrt. */
  pendingEditId?: number;
  /**
   * Was die **laufende** Hervorhebung bisher gebracht hat; fehlt, wenn gerade keine läuft.
   * Bewusst ohne Verifizierung sichtbar — wer für Sichtbarkeit zahlt, sieht ihre Wirkung.
   */
  promotionStats?: PromotionStats;
  /**
   * Gelaufen und ohne neuen Termin: hier gehört der Termin des Folgejahres hinein, statt in ein
   * zweites Event. Diese Events kommen auch dann mit, wenn die Liste keine Vergangenheit zeigt.
   */
  nextEditionDue?: boolean;
}

/**
 * Wirkung einer laufenden Hervorhebung über ihre bisherige Laufzeit.
 *
 * Nur Zahlen mit Zeitbezug. Favoriten fehlen hier absichtlich: der Favoritenzähler am Event ist
 * ein Gesamtstand, und neben zwei Zeitraum-Zahlen gestellt behauptete er einen Zusammenhang, den
 * es nicht gibt.
 */
export interface PromotionStats {
  /** Beginn der laufenden Buchung und heutiges Datum, ISO — der gemessene Zeitraum. */
  from: string;
  to: string;
  views: number;
  uniqueVisitors: number;
  clicks: number;
}

/** Zahlen zu einem Event, wie sie sein Veranstalter sieht. */
export interface OrganizerInsightRow {
  eventId: number;
  title: string;
  eventDate: string;
  views: number;
  uniqueVisitors: number;
  clicks: number;
  icalDownloads: number;
  favorites: number;
}

/**
 * Wie viele Menschen wir per E-Mail über die Events dieses Veranstalters erreicht haben, je
 * Mail-Art. „42 Erinnerungen" und „42 Absagen" sind nicht dasselbe Ereignis.
 */
export interface OrganizerNotificationRow {
  mailType: string;
  recipients: number;
}

export interface OrganizerInsights {
  from: string;
  to: string;
  rows: OrganizerInsightRow[];
  notifications: OrganizerNotificationRow[];
  totalNotified: number;
  totalViews: number;
  totalUniqueVisitors: number;
  totalClicks: number;
  totalFavorites: number;
  /** Wahr, wenn nur Browser-Besuche enthalten sind. */
  webOnly: boolean;
}

/** Ein Ziel, zu dem von der Event-Seite weitergeklickt wurde, mit seiner Klickzahl. */
export interface EventClickTarget {
  /** Tracking-Kontext, z. B. "anmeldung", "ausschreibung", "veranstalter-seite", "link:WEBSITE". */
  context: string;
  clicks: number;
}

/**
 * Die Zahlen zu einem Event auf seiner Detailseite (`GET /events/{id}/stats`) — über die ganze
 * erfasste Zeit, nicht über ein Fenster. Sichtbar für Admin/Moderator und für Verwalter des
 * Veranstalters mit Premium; alle anderen bekommen 404 bzw. 402.
 */
export interface EventDetailStats {
  /** Erster erfasster Tag (ISO); null, wenn noch nichts gemessen wurde. */
  since: string | null;
  views: number;
  /** Nur Aufrufer, je Tag einmal gezählt. */
  uniqueViewers: number;
  clicks: number;
  uniqueClickers: number;
  /** Kalender-Downloads (.ics). */
  calendarAdds: number;
  favorites: number;
  /**
   * Über „Benachrichtigen“ eingeschaltet, in der laufenden Auflage. Steckt auch in `favorites`,
   * denn die Glocke merkt das Event vor. Fehlt bei einem älteren Backend.
   */
  registrationAlerts?: number;
  /** Wohin die Klicks gingen, absteigend nach Klicks. */
  clickTargets: EventClickTarget[];
  /** Termin der laufenden Auflage; die Zahlen oben gelten seit dem Vorrücken auf ihn. */
  editionDate: string;
  /** Frühere Auflagen, jüngste zuerst, jede mit den Zahlen ihres eigenen Zeitfensters. */
  previousEditions: EventEditionStats[];
}

/** Zahlen einer früheren Auflage (siehe EventEditionService im Backend). */
export interface EventEditionStats {
  eventDate: string;
  /** Erster gemessener Tag im Zeitfenster; null, wenn darin nichts gemessen wurde. */
  since: string | null;
  views: number;
  uniqueViewers: number;
  clicks: number;
  calendarAdds: number;
  /** Stand der Vormerkungen beim Vorrücken. */
  favorites: number;
}

/** Eine abgeschlossene Auflage eines Events, wie sie die Veranstalterseite zeigt. */
export interface EventEdition {
  id: number;
  eventId: number;
  title: string;
  eventDate: string;
  endDate: string | null;
  city: string | null;
  cancelled: boolean;
  distanceKms: number[];
  /** Flyer dieser Auflage, wenn einer abgelegt wurde. */
  flyerUrl: string | null;
  /** Teilnehmer dieser Auflage, wenn gepflegt. */
  participants?: number | null;
}

export interface OrganizerPortalSubmissions {
  eventEdits: EventEditResponse[];
  organizerEdits: OrganizerEditResponse[];
}


/**
 * Ein hochgeladenes Logo oder Banner, das auf Freigabe wartet.
 *
 * Ohne Bytes: die Liste ist eine Übersicht, das Bild selbst holt die Prüfansicht einzeln nach.
 */
export interface OrganizerAssetProposal {
  id: number;
  organizerId: number;
  organizerName?: string;
  kind: 'LOGO' | 'BANNER';
  contentType: string;
  bytes: number;
  uploadedBy?: string;
  createdAt?: string;
}

/** Treffer der schmalen Nutzersuche in der Verknuepfungs-Maske. */
export interface UserLookupResult {
  id: number;
  username: string;
  email?: string;
}

export interface EventDistance {
  id: number;
  distanceKm: number;
  elevationMeters: number;
  routeUrl?: string;
  label?: string;
  hasGpx?: boolean;
  /**
   * Zeit-Variante (z. B. 24h-Rennen): gesetzt = zeitlimitiert statt distanzbasiert.
   * distanceKm ist dann optional die Rundenlänge (Rundkurs), sonst 0.
   */
  durationMinutes?: number | null;
  /**
   * Eigene Eventart dieser Strecke (Gravel-Race neben Gravel-Tour im selben Event).
   * null/undefined = es gilt die Eventart des Events.
   */
  eventType?: EventType | null;
  /** Eigene Startzeit dieser Strecke (HH:mm). null/undefined = es gilt die Startzeit des Events. */
  startTime?: string | null;
}

export interface CyclingEvent {
  participating?: boolean;
  friendInterests?: import('./community').FriendInterest[];
  id: number;
  title: string;
  description?: string;
  address: string;
  locationName?: string;
  street?: string;
  houseNumber?: string;
  postalCode: string;
  city: string;
  bundesland: string;
  eventDate: string;
  /** Optional last day of a multi-day event. Absent = single-day event (ends on eventDate). */
  endDate?: string;
  startTime?: string;
  startTimeEnd?: string;
  /** Teilnehmer der letzten Auflage, wenn gepflegt (ganze Zahl, nie 0). */
  participants?: number;
  organizer?: string;
  organizerId?: number;
  category: EventCategory;
  /**
   * Weitere Kategorien, für die die Veranstaltung offen ist (z. B. ein Volksradfahren, das mit
   * Rennrad, Gravel und MTB gefahren werden kann). Enthält nie `category` selbst — die
   * vollständige Liste liefert `eventCategories()`.
   */
  secondaryCategories?: EventCategory[];
  eventType?: EventType;
  organizerUrl?: string;
  /** Flyer des Events: hochgeladene Datei, sonst die hinterlegte externe URL. */
  flyerUrl?: string;
  /** Die rohe externe Flyer-URL — hieran bindet das Edit-Formular, nicht an das aufgeloeste flyerUrl. */
  flyerUrlExternal?: string;
  /** Es existiert ein hochgeladener Flyer (verdeckt flyerUrlExternal). */
  flyerUploaded?: boolean;
  /**
   * Content-Type des hochgeladenen Flyers ("image/jpeg", "application/pdf", …); fehlt bei einer
   * externen URL. Nur Bilder lassen sich einbetten — alles andere wird verlinkt (`isFlyerImage`).
   */
  flyerContentType?: string;
  /** Hero-Bild des Events: hochgeladenes Bild, sonst die hinterlegte externe URL. */
  imageUrl?: string;
  /** Die rohe externe Bild-URL — hieran bindet das Edit-Formular, nicht an das aufgeloeste imageUrl. */
  imageUrlExternal?: string;
  /** Es existiert ein hochgeladenes Bild (verdeckt imageUrlExternal). */
  imageUploaded?: boolean;
  imageCredit?: string;
  /** Hero-Bild ist KI-generiert — wird im Frontend sichtbar gekennzeichnet. */
  imageAiGenerated?: boolean;
  /** Fokuspunkt in Prozent (0-100): was im Zuschnitt sichtbar bleiben muss. Default Mitte. */
  imageFocusX?: number;
  imageFocusY?: number;
  /** Logo des Veranstalters (Badge auf dem Hero). */
  organizerLogoUrl?: string;
  /** Header-Foto des Veranstalters — Hero-Fallback, wenn das Event kein eigenes Bild hat. */
  organizerPhotoUrl?: string;
  fullyBooked: boolean;
  cancelled: boolean;
  /** Termin steht noch nicht endgueltig fest. */
  tentative: boolean;
  /**
   * Permanentes Event ohne festen Termin (Stoneman, Permanents). `eventDate` ist dann nur ein
   * Platzhalter (1970-01-01) und wird nirgends gezeigt — kein Countdown, kein Datum-Kopf, kein
   * „vergangen". Siehe docs/bereiche/permanente-events.md.
   */
  permanent?: boolean;
  /**
   * Saison eines permanenten Events als Monate 1–12 (`permanentSeason.ts`), beide leer =
   * ganzjährig. Bei einem Termin immer leer.
   */
  seasonFrom?: number | null;
  seasonUntil?: number | null;
  /**
   * Gebiet eines permanenten Events ohne festen Startort („Erzgebirge", „Dolomiten"). Gilt nur
   * zusammen mit `permanent` und wie die Saison: wer `permanent` mitschickt, schickt das Gebiet mit.
   */
  areaName?: string | null;
  /**
   * Ausdehnung des Gebiets aus dem Geocoder — nur gesetzt, wenn das Event über sein Gebiet statt
   * über Ort/PLZ verortet ist. `latitude`/`longitude` sind dann der Mittelpunkt.
   */
  areaBounds?: AreaBounds | null;
  /**
   * Beginn der Anmeldung als abgeschnittenes ISO-Datum („2026-12-01", „2026-12", „2027") —
   * siehe `formatRegistrationStart`. **Nur in Admin-/Mod-Antworten enthalten**: die Angabe ist
   * eine interne Notiz und steht bewusst nicht auf der öffentlichen Event-Seite.
   */
  registrationStart?: string;
  /** Anmeldeart — wie `registrationStart` nur in Admin-/Mod-Antworten enthalten. */
  registrationMode?: RegistrationMode;
  /** Von Hand gesetzter Stand („läuft bereits"/„geschlossen") — nur in Admin-/Mod-Antworten. */
  registrationState?: RegistrationState;
  /** Öffentlicher Anmeldestand (abgeleitet, ohne Termin) — steht in jeder Antwort. */
  registrationStatus?: RegistrationStatus;
  /**
   * Kann noch eine Anmeldungs- oder Preis-Mail kommen? Steuert die Glocke. Rechnet das Backend,
   * weil der Anmeldestart intern bleibt.
   */
  registrationNoticeAvailable?: boolean;
  /** Preisstufen der Anmeldung, nach Frist sortiert (siehe `priceTiers.ts`). */
  priceTiers?: EventPriceTier[];
  status: EventStatus;
  externalId?: string;
  sourceUrl?: string;
  active: boolean;
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
  distances: EventDistance[];
  komootCollectionUrl?: string;
  favorited: boolean;
  latitude?: number;
  longitude?: number;
  favoriteCount: number;
  /** 0..100 completeness score; only present in admin responses. */
  enrichmentScore?: number;
  /** Per-field-group breakdown of the enrichment score; only present in admin responses. */
  scoreBreakdown?: ScoreComponent[];
  sources?: string[];
  links?: EventLink[];
  /** @deprecated Nur noch die URLs der sichtbaren Links — für Alt-Consumer. Nutze `links`. */
  additionalLinks?: string[];
  /**
   * Bezahlte Hervorhebung der Card in den Ergebnislisten. Öffentlich steht hier nur eine
   * *laufende* Buchung; Admin/Moderation sehen sie immer, mit `active` als Stand. Zum Zeichnen
   * nie direkt lesen, sondern `activePromotion(event)` — das prüft `active` mit.
   */
  promotion?: EventPromotion;
}

/**
 * Stufe der bezahlten Hervorhebung (Design-Handoff „Event-Card Hervorhebung"):
 * - `FRAME` = 1a „Signatur-Rahmen": Markenrahmen, hell getönter Grund, Label im Kopf.
 * - `INVERTED` = 1c „Invertierte Card": dunkler Grund, goldenes Label mit Puls-Punkt.
 * - `STAGE` = 1b „Bühne": über beide Rasterspalten, Foto links, Knopf zur Ausschreibung; auf
 *   einer Spalte (Mobilansicht und App) wird sie hoch statt breit — Entwurfsvariante 2a.
 *
 * `FRAME` und `INVERTED` behalten exakt die Grundfläche einer normalen Card. `STAGE` verlässt das
 * Raster als einzige Stufe und darf deshalb **nur einmal je Ergebnisseite** erscheinen. Keine
 * Stufe ändert etwas an der Sortierung — eine gebuchte Card bleibt an ihrer chronologischen
 * Position.
 */
export type PromotionTier = 'FRAME' | 'INVERTED' | 'STAGE';

export interface EventPromotion {
  tier: PromotionTier;
  /** Kennzeichnungstext auf der Card, i. d. R. „ANZEIGE". Nie leer. */
  label: string;
  /** ISO-8601-Zeitpunkte. Gerechnet wird serverseitig, der Client zeigt sie nur an. */
  from: string;
  until: string;
  /** Läuft der gebuchte Zeitraum gerade? Nur dann wird die Card hervorgehoben gezeichnet. */
  active: boolean;
  /**
   * Reicht die Buchung bis zum Ende der Veranstaltung? Kommt fertig vom Server
   * (`EventPromotionResponse.coversEvent`) — die Grenze ist Mitternacht **deutscher** Ortszeit,
   * und die kann ein Browser nicht kennen, ohne sie nachzubauen. Optional, weil ältere
   * Antworten das Feld noch nicht tragen.
   */
  coversEvent?: boolean;
}

/** Setzt/ersetzt die Hervorhebung. Genau eines von `days` und `untilEvent` angeben. */
export interface SetEventPromotionRequest {
  tier: PromotionTier;
  /** Leer = Voreinstellung der Stufe (`PROMOTION_DEFAULT_LABELS`). */
  label?: string;
  days?: number;
  untilEvent?: boolean;
}

/**
 * Die Hervorhebung, sofern sie gerade läuft — sonst null. Einziger Weg, an dem Karten und Listen
 * entscheiden, ob sie hervorgehoben zeichnen: die Admin-Ansichten bekommen auch geplante und
 * abgelaufene Buchungen geliefert, und die dürfen die öffentliche Card nicht verändern.
 */
export function activePromotion(event: { promotion?: EventPromotion }): EventPromotion | null {
  return event.promotion && event.promotion.active ? event.promotion : null;
}

/**
 * Welche Hülle tatsächlich gezeichnet wird — nicht immer die gebuchte.
 *
 * Die Bühne (`STAGE`) besteht zur Hälfte aus dem Event-Foto. Fehlt es (weil es nach der Buchung
 * entfernt wurde — buchen lässt sie sich ohne Foto gar nicht), bliebe eine doppelt breite, halb
 * leere Card. Dann fällt sie auf `FRAME` zurück, wie im Handoff vorgesehen. Web und App treffen
 * diese Entscheidung deshalb an genau einer Stelle.
 */
export function promotionRenderTier(
  event: { promotion?: EventPromotion; imageUrl?: string },
): PromotionTier | null {
  const promo = activePromotion(event);
  if (!promo) return null;
  if (promo.tier === 'STAGE' && !event.imageUrl) return 'FRAME';
  return promo.tier;
}

/**
 * Läuft die gebuchte Hervorhebung schon bis zum Ende der Veranstaltung?
 *
 * Dann gibt es an Laufzeit nichts mehr zu kaufen — mehr als „bis zum Renntag" bringt keinem etwas,
 * die Card verschwindet danach ohnehin aus den Listen. Nur ein Wechsel auf eine stärkere Stufe
 * wäre noch ein Angebot.
 *
 * **Gerechnet wird das im Backend** (`promotionEndOfEvent`, Europe/Berlin); hier steht nur noch
 * das Auslesen. Die frühere Fassung baute die Grenze aus `eventDate`/`endDate` nach und traf
 * dabei Mitternacht in der Zone des *Browsers* — aus dem Ausland also den falschen Zeitpunkt,
 * und zwar bei genau den Buchungen, die exakt bis zum Ende laufen.
 */
export function promotionCoversEvent(event: { promotion?: EventPromotion }): boolean {
  return activePromotion(event)?.coversEvent === true;
}

/**
 * Ziel des Knopfes auf der Bühne. Bewusst kein eigenes Feld an der Buchung: das Event weiß
 * bereits, wohin sich Teilnehmer wenden sollen. Anmeldung schlägt Ausschreibung — wer klickt,
 * will sich meist anmelden.
 *
 * `null` heißt: kein Knopf. Die Chips rücken dann allein in die Fußzeile.
 */
export function promotionCta(
  event: { links?: EventLink[] },
): { url: string; label: string } | null {
  const links = (event.links ?? []).filter(l => l.visibility === 'PUBLIC');
  const registration = links.find(l => l.type === 'REGISTRATION');
  if (registration) return { url: registration.url, label: 'Zur Anmeldung' };
  const announcement = links.find(l => l.type === 'ANNOUNCEMENT');
  if (announcement) return { url: announcement.url, label: 'Zur Ausschreibung' };
  return null;
}

/**
 * Lässt sich der Flyer als Bild einbetten? Nur dann darf er in ein `<img>` — ein PDF ergäbe dort
 * ein kaputtes Bild statt eines Flyers.
 *
 * Bei einem Upload steht der Typ fest (`flyerContentType`, aus den Magic Bytes bestimmt). Bei
 * einer externen URL kennen wir ihn nicht und bleiben beim bisherigen Verhalten „ist ein Bild" —
 * nur eine URL, die sichtbar auf .pdf endet, wird verlinkt statt eingebettet.
 */
export function isFlyerImage(event: { flyerUrl?: string; flyerContentType?: string }): boolean {
  if (!event.flyerUrl) return false;
  if (event.flyerContentType) return event.flyerContentType.indexOf('image/') === 0;
  return !/\.pdf($|[?#])/i.test(event.flyerUrl);
}

/** Art eines Event-Links. Steuert Label/Icon in der UI. */
export type LinkType =
  | 'REGISTRATION'   // Anmeldung
  | 'ANNOUNCEMENT'   // Ausschreibung
  | 'WEBSITE'        // Event-/Veranstalter-Seite
  // Eigene Kanäle *des Events* — groessere Veranstaltungen pflegen die unabhaengig von
  // den Kanaelen des Veranstalters. Die des Veranstalters stehen am Organizer, nicht hier.
  | 'INSTAGRAM'
  | 'FACEBOOK'
  | 'SOURCE'         // Quellseite
  | 'BREITENSPORT'   // rad-net Breitensport-Kalender
  | 'OTHER';

/** PUBLIC = auf der Event-Seite für alle sichtbar. INTERNAL = nur Mods/Admins. */
export type LinkVisibility = 'PUBLIC' | 'INTERNAL';

/**
 * Eine Preisstufe der Anmeldung („Frühbucher bis 31.12."). `validUntil` ist der letzte Tag der
 * Stufe als ISO-Datum, danach wird es teurer. Bewusst ohne Betrag (V179).
 */
export interface EventPriceTier {
  label?: string | null;
  validUntil: string;
}

export interface EventLink {
  id?: number;
  url: string;
  type: LinkType;
  visibility: LinkVisibility;
  label?: string;
}

/** Tages-Wettervorhersage für einen Event-Tag (DWD via Bright Sky). */
export interface WeatherForecast {
  /** Event-Tag, yyyy-MM-dd. */
  date: string;
  tempMin: number;
  tempMax: number;
  /** Niederschlag als Tagessumme in mm. */
  precipitation: number;
  /** Maximale Windgeschwindigkeit in km/h. */
  windMax: number;
  /** Bright-Sky-Icon-Key (z.B. 'clear-day', 'rain'); via weatherEmoji() in ein Emoji übersetzen. */
  icon: string;
  /** Grobe DWD-Kategorie (z.B. 'dry', 'rain'); via weatherLabel() in ein Label übersetzen. */
  condition: string;
  source: string;
}

/** One weighted field-group of the enrichment score (see {@link CyclingEvent.scoreBreakdown}). */
export interface ScoreComponent {
  key: string;
  label: string;
  /** Max points this group can contribute. */
  weight: number;
  /** Points actually earned — either 0 or the full weight. */
  earned: number;
}

export interface DistanceRequest {
  /** Set when editing an existing distance — keeps its id (and uploaded GPX) stable across saves. */
  id?: number;
  distanceKm: number;
  elevationMeters: number;
  routeUrl?: string;
  label?: string;
  /** Read-only hint for form UIs; ignored by the backend. */
  hasGpx?: boolean;
  /** Zeit-Variante: Zeitlimit in Minuten (z. B. 1440 = 24h). null/undefined = distanzbasiert. */
  durationMinutes?: number | null;
  /** Eigene Eventart der Strecke; leer = die des Events. */
  eventType?: EventType | null;
  /** Eigene Startzeit der Strecke (HH:mm); leer = die des Events. */
  startTime?: string | null;
}

export interface CreateEventRequest {
  title: string;
  description?: string;
  locationName?: string;
  street?: string;
  houseNumber?: string;
  postalCode: string;
  city: string;
  bundesland: string;
  eventDate: string;
  endDate?: string;
  startTime?: string;
  startTimeEnd?: string;
  /** Teilnehmer der letzten Auflage; leer oder 0 = keine Angabe. */
  participants?: number;
  organizer?: string;
  organizerId?: number;
  category: EventCategory;
  /** Weitere Kategorien der Veranstaltung. `category` darf mit drin stehen (Backend filtert). */
  secondaryCategories?: EventCategory[];
  eventType?: EventType;
  organizerUrl?: string;
  flyerUrl?: string;
  imageUrl?: string;
  imageCredit?: string;
  imageAiGenerated?: boolean;
  imageFocusX?: number;
  imageFocusY?: number;
  fullyBooked?: boolean;
  cancelled?: boolean;
  tentative?: boolean;
  /** Permanentes Event — dann darf `eventDate` leer bleiben, ebenso Ort und PLZ. */
  permanent?: boolean;
  /**
   * Saison als Monate 1–12, beide leer = ganzjährig. Zählt nur zusammen mit `permanent`: wer
   * `permanent` mitschickt, schickt die Saison mit (fehlt sie, ist das Event ganzjährig).
   */
  seasonFrom?: number | null;
  seasonUntil?: number | null;
  /**
   * Gebiet eines permanenten Events ohne festen Startort („Erzgebirge", „Dolomiten"). Gilt nur
   * zusammen mit `permanent` und wie die Saison: wer `permanent` mitschickt, schickt das Gebiet mit.
   */
  areaName?: string | null;
  /**
   * Anmeldestart als abgeschnittenes ISO-Datum. Beim Aktualisieren gilt: nicht mitgeschickt =
   * unverändert, `''` löscht ihn. Nur Mods/Admins dürfen ihn setzen.
   */
  registrationStart?: string;
  registrationMode?: RegistrationMode;
  /** Gesetzter Stand — Alternative zum Anmeldestart; er leert den Termin. */
  registrationState?: RegistrationState;
  distances: DistanceRequest[];
  komootCollectionUrl?: string;
  links?: EventLink[];
  /** Beim Aktualisieren: fehlt = unverändert, leere Liste = alle Preisstufen löschen. */
  priceTiers?: EventPriceTier[];
}

export interface UpdateEventRequest extends CreateEventRequest {
  status?: EventStatus;
  active: boolean;
  /**
   * Beim Vorrücken ins nächste Jahr den bisherigen Stand als Auflage ablegen. Weggelassen heißt
   * automatisch (alter Termin zurück, neuer dahinter); false unterdrückt es bewusst.
   */
  newEdition?: boolean;
}

export enum DuplicateReason {
  DUPLICATE = 'DUPLICATE',
  RECURRING_SERIES = 'RECURRING_SERIES',
}

export const DUPLICATE_REASON_LABELS: Record<DuplicateReason, string> = {
  [DuplicateReason.DUPLICATE]: 'Duplikat',
  [DuplicateReason.RECURRING_SERIES]: 'Fortlaufende Serie',
};

export interface DuplicateGroup {
  events: CyclingEvent[];
  // Absent on older API responses → Aufrufer behandeln fehlend wie DUPLICATE.
  reason?: DuplicateReason;
}

export interface MergeEventsRequest {
  primaryId: number;
  duplicateIds: number[];
  mergedData: UpdateEventRequest;
}

export interface DismissDuplicatesRequest {
  eventIds: number[];
}

export interface OrganizerDuplicateGroup {
  organizers: Organizer[];
}

export interface MergeOrganizersRequest {
  primaryId: number;
  duplicateIds: number[];
}

export interface DismissOrganizerDuplicatesRequest {
  organizerIds: number[];
}

// Event Edit types

export enum EventEditType {
  CREATE = 'CREATE',
  EDIT = 'EDIT'
}

export enum EventEditStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  /**
   * In einem anderen Vorschlag aufgegangen (`mergedInto`): durch einen neueren desselben
   * Absenders ersetzt oder beim Übernehmen eines anderen mit erledigt.
   */
  MERGED = 'MERGED'
}

/** Kurzform eines anderen offenen Vorschlags, den man beim Übernehmen mit erledigen kann. */
export interface EventEditRef {
  id: number;
  type: EventEditType;
  title: string;
  eventDate: string;
  createdAt?: string | null;
  /** Konto, Name oder Adresse des Einreichenden; null = anonym. */
  submittedBy?: string | null;
  /** Foto, Flyer oder GPX daran. Sie verfallen beim Mit-Erledigen. */
  hasAttachments?: boolean;
}

/**
 * Was sonst noch zu demselben Event vorliegt. `otherPendingCount`: weitere offene Vorschläge.
 * `newerApprovedAt`: ein später eingereichter Vorschlag ist schon übernommen, dieser hier würde
 * dessen Werte wieder überschreiben.
 */
export interface EventEditSiblings {
  otherPendingCount: number;
  newerApprovedAt?: string | null;
  /**
   * Was beim Übernehmen mit erledigt werden kann: die anderen offenen Vorschläge zu diesem Event
   * und neue Events desselben Absenders am selben Tag.
   */
  mergeCandidates?: EventEditRef[];
}

/** Antwort von `GET /events/notify-count`. */
export interface NotifyCount {
  count: number;
  /** Zu einem der Events ging heute schon eine Änderungs-Mail raus; eine weitere geht nicht. */
  alreadyNotifiedToday: boolean;
}

/**
 * Antwort von `POST /events/{id}/notify-preview`: dieselben Felder wie [NotifyCount], gerechnet
 * für einen bestimmten Formularstand. Löst die Änderung keine Mail aus (etwa nur eine neue
 * Preisstufe), ist `count` 0 und der Dialog bleibt zu.
 */
export interface NotifyPreview extends NotifyCount {
  wouldNotify: boolean;
}

export interface EventEditResponse {
  id: number;
  eventId?: number;
  /** Nur für die Redaktion gefüllt. */
  siblings?: EventEditSiblings | null;
  type: EventEditType;
  status: EventEditStatus;
  submittedBy?: number;
  submittedByUsername?: string;
  /** Gesetzt, wenn die Einreichung vom verknuepften Veranstalter kam (serverseitig abgeleitet). */
  submittedAsOrganizerId?: number;
  submittedAsOrganizerName?: string;
  /**
   * Gewünschter Zustand nach der Freigabe: `DRAFT` (Veranstalter hat „Als Entwurf speichern"
   * gesetzt) oder `PUBLISHED` („Veröffentlichen" an einem Entwurf). Fehlt = Standard.
   */
  targetStatus?: EventStatus;
  submitterName?: string;
  submitterEmail?: string;
  reviewedBy?: number;
  reviewedByUsername?: string;
  reviewComment?: string;
  /** Belege eines Agenten-Vorschlags (Zitate mit Fundstelle), nur Prüfansicht. */
  evidence?: string | null;
  title: string;
  description?: string;
  address: string;
  locationName?: string;
  street?: string;
  houseNumber?: string;
  postalCode: string;
  city: string;
  bundesland: string;
  eventDate: string;
  /** Optional last day of a multi-day event. Absent = single-day event (ends on eventDate). */
  endDate?: string;
  startTime?: string;
  startTimeEnd?: string;
  /** Vorgeschlagene Teilnehmerzahl der letzten Auflage; fehlt = keine Angabe. */
  participants?: number;
  organizer?: string;
  organizerId?: number;
  newOrganizerName?: string;
  newOrganizerContactPerson?: string;
  newOrganizerEmail?: string;
  newOrganizerPhone?: string;
  newOrganizerWebsite?: string;
  category: EventCategory;
  /**
   * Weitere Kategorien, für die die Veranstaltung offen ist (z. B. ein Volksradfahren, das mit
   * Rennrad, Gravel und MTB gefahren werden kann). Enthält nie `category` selbst — die
   * vollständige Liste liefert `eventCategories()`.
   */
  secondaryCategories?: EventCategory[];
  eventType?: EventType;
  organizerUrl?: string;
  flyerUrl?: string;
  /** Bildnachweis zum vorgeschlagenen Foto. */
  imageCredit?: string;
  /** Am Vorschlag hängt ein Foto — abrufbar über GET /event-edits/{id}/image (nur Mod/Admin). */
  hasImage?: boolean;
  /** Am Vorschlag hängt ein Flyer — abrufbar über GET /event-edits/{id}/flyer (nur Mod/Admin). */
  hasFlyer?: boolean;
  /**
   * Ids der vorgeschlagenen Distanzen (`distances[].id`), zu denen eine GPX-Datei eingereicht
   * wurde — abrufbar über GET /event-edits/{id}/distances/{distanceId}/gpx (nur Mod/Admin).
   */
  gpxDistanceIds?: number[];
  fullyBooked: boolean;
  cancelled: boolean;
  tentative: boolean;
  /**
   * Permanentes Event (siehe `CyclingEvent.permanent`). `null` = der Vorschlag sagt nichts dazu
   * (vor der Einführung eingereicht); die Freigabe lässt das Merkmal dann, wie es ist.
   */
  permanent?: boolean | null;
  /** Vorgeschlagene Saison, gilt wie `permanent` nur, wenn `permanent` gesetzt ist. */
  seasonFrom?: number | null;
  seasonUntil?: number | null;
  /**
   * Gebiet eines permanenten Events ohne festen Startort („Erzgebirge", „Dolomiten"). Gilt nur
   * zusammen mit `permanent` und wie die Saison: wer `permanent` mitschickt, schickt das Gebiet mit.
   */
  areaName?: string | null;
  /** Vorgeschlagener Anmeldestart als abgeschnittenes ISO-Datum, fehlt = keine Angabe. */
  registrationStart?: string;
  registrationMode?: RegistrationMode;
  registrationState?: RegistrationState;
  createdAt?: string;
  updatedAt?: string;
  reviewedAt?: string;
  /** Bei Status `MERGED`: der Vorschlag, in dem dieser aufgegangen ist. */
  mergedInto?: number | null;
  distances: EventDistance[];
  links?: EventLink[];
  /** Vorgeschlagene Preisstufen; fehlt = nicht übermittelt, die des Events bleiben. */
  priceTiers?: EventPriceTier[] | null;
  originalEvent?: CyclingEvent;
  possibleDuplicates?: DuplicateHint[];
}

/**
 * EVENT und PROPOSAL meinen „zwei Mal dasselbe". EDITION meint dieselbe Veranstaltung ein Jahr
 * weiter: der Vorschlag ist richtig, gehört aber als neuer Termin an das vorhandene Event.
 */
export type DuplicateHintKind = 'EVENT' | 'PROPOSAL' | 'EDITION';

export interface DuplicateHint {
  kind: DuplicateHintKind;
  id: number;
  title: string;
  eventDate: string;
  city?: string;
  /** Bei EVENT/EDITION: „Bereits vorhanden" bzw. „Andere Auflage"; bei PROPOSAL: Einreicher-Name. */
  origin?: string;
}

export interface NewOrganizerData {
  name: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  website?: string;
}

/** Eine Zeile der Anmeldestart-Übersicht (`GET /events/registration-starts`, Mod+Admin). */
export interface RegistrationStartEntry {
  eventId: number;
  title: string;
  /**
   * Abgeschnittenes ISO-Datum — die Genauigkeit steckt in der Länge, siehe
   * `formatRegistrationStart`. Fehlt bei gesetztem `registrationState`: dann steht der Stand fest,
   * ohne dass wir den Termin kennen.
   */
  registrationStart?: string | null;
  /** Auf Monats-/Jahresanfang normalisiertes Datum; danach ist die Liste sortiert. */
  registrationStartDate?: string | null;
  /** Gesetzter Stand; „läuft bereits" steht ganz oben in der Übersicht. */
  registrationState: RegistrationState;
  registrationMode: RegistrationMode;
  /** Der öffentliche Anmelde-Link des Events, falls hinterlegt. */
  registrationUrl?: string | null;
  eventDate: string;
  city: string;
  bundesland: string;
  status: EventStatus;
  favoriteCount: number;
  /** Ausgebucht/abgesagt — dann ist der Anmeldetermin nur noch der Vollständigkeit halber da. */
  fullyBooked: boolean;
  cancelled: boolean;
  /** Klicks auf den Anmelde-Link (gesamt) und die Zahl der Personen dahinter. */
  registrationClicks: number;
  registrationClickers: number;
}

/** Wie viele Konten Events merken (`/events/most-favorited/accounts`), für „Beliebteste Events". */
export interface FavoriteAccounts {
  /** Aktive Konten insgesamt. */
  accounts: number;
  /** Aktive Konten mit mindestens einem gemerkten kommenden Event. */
  withUpcoming: number;
  /** Dasselbe, vergangene Events eingeschlossen. */
  withAny: number;
}

/** Die Übersicht samt Abdeckung: wie viele der kommenden Events tragen überhaupt einen Anmeldestart. */
export interface RegistrationStartOverview {
  entries: RegistrationStartEntry[];
  upcomingEvents: number;
  /** Events mit Anmeldestart oder „läuft bereits" — genau die Zeilen in `entries`. */
  withRegistrationStart: number;
  /** Kommende Events mit Anmeldung nur vor Ort (haben bewusst weder Termin noch Link). */
  onsiteOnly: number;
  /** Kommende Events mit geschlossener Anmeldung — nicht in der Liste, es gibt nichts zu öffnen. */
  closed: number;
}

export interface ProposeEventRequest {
  title: string;
  description?: string;
  locationName?: string;
  street?: string;
  houseNumber?: string;
  postalCode: string;
  city: string;
  bundesland: string;
  eventDate: string;
  /** Optional last day of a multi-day event. Absent = single-day event (ends on eventDate). */
  endDate?: string;
  startTime?: string;
  startTimeEnd?: string;
  /** Teilnehmer der letzten Auflage; ein Vorschlag kann sie setzen, nicht leeren. */
  participants?: number;
  organizer?: string;
  organizerId?: number;
  /** A brand-new organizer submitted inline; created and linked when the proposal is approved. */
  newOrganizer?: NewOrganizerData;
  category: EventCategory;
  /** Weitere Kategorien der Veranstaltung. `category` darf mit drin stehen (Backend filtert). */
  secondaryCategories?: EventCategory[];
  eventType?: EventType;
  organizerUrl?: string;
  /** Flyer-/Bild-URL. Nicht gesetzt = kein Flyer vorgeschlagen (löscht keinen vorhandenen). */
  flyerUrl?: string;
  /** Bildnachweis zum Foto, das direkt nach dem Einreichen hochgeladen wird. */
  imageCredit?: string;
  /**
   * Es folgt ein Foto-Upload zu diesem Vorschlag. Nur dafür da, die „keine Änderungen"-Prüfung
   * des Backends zu überspringen — ein reiner Bildvorschlag ändert sonst kein Feld.
   */
  imagePending?: boolean;
  /**
   * Es folgt mindestens ein GPX-Upload zu diesem Vorschlag. Wie `imagePending` nur dafür da, die
   * „keine Änderungen"-Prüfung zu überspringen — ein reiner Streckenvorschlag ändert kein Feld.
   */
  gpxPending?: boolean;
  /**
   * Es folgt ein Flyer-Upload zu diesem Vorschlag. Wie `imagePending` nur dafür da, die
   * „keine Änderungen"-Prüfung zu überspringen — ein reiner Flyer-Vorschlag ändert kein Feld.
   */
  flyerPending?: boolean;
  /**
   * „Als Entwurf speichern" (Veranstalter-Bereich): das Event soll nach der Freigabe nicht
   * öffentlich stehen. Zählt nur, wenn der Vorschlag vom verknüpften Veranstalter kommt; beim
   * Bearbeiten eines Entwurfs heißt `false` „jetzt veröffentlichen".
   */
  saveAsDraft?: boolean;
  fullyBooked?: boolean;
  cancelled?: boolean;
  tentative?: boolean;
  /** Permanentes Event — dann darf `eventDate` leer bleiben, ebenso Ort und PLZ. */
  permanent?: boolean;
  /**
   * Saison als Monate 1–12, beide leer = ganzjährig. Zählt nur zusammen mit `permanent`: wer
   * `permanent` mitschickt, schickt die Saison mit (fehlt sie, ist das Event ganzjährig).
   */
  seasonFrom?: number | null;
  seasonUntil?: number | null;
  /**
   * Gebiet eines permanenten Events ohne festen Startort („Erzgebirge", „Dolomiten"). Gilt nur
   * zusammen mit `permanent` und wie die Saison: wer `permanent` mitschickt, schickt das Gebiet mit.
   */
  areaName?: string | null;
  /**
   * Anmeldestart als abgeschnittenes ISO-Datum. Optional: fehlt er, bleibt ein vorhandener beim
   * Freigeben unangetastet — ein Vorschlag ohne Angabe löscht ihn nicht.
   */
  registrationStart?: string;
  registrationMode?: RegistrationMode;
  /** Gesetzter Stand — Alternative zum Termin. */
  registrationState?: RegistrationState;
  distances: DistanceRequest[];
  /** Vorgeschlagene Links. Aus dem öffentlichen Formular immer PUBLIC (Backend erzwingt das). */
  links?: EventLink[];
  /** Fehlt = unverändert, leere Liste = löschen. */
  priceTiers?: EventPriceTier[];
  submitterName?: string;
  submitterEmail?: string;
}

export interface ReviewEditOverrides {
  title: string;
  description?: string;
  locationName?: string;
  street?: string;
  houseNumber?: string;
  postalCode: string;
  city: string;
  bundesland: string;
  eventDate: string;
  /** Optional last day of a multi-day event. Absent = single-day event (ends on eventDate). */
  endDate?: string;
  startTime?: string;
  startTimeEnd?: string;
  /** Vom Reviewer korrigierte Teilnehmerzahl. Fehlt = die des Vorschlags behalten. */
  participants?: number;
  organizer?: string;
  organizerId?: number;
  category: EventCategory;
  /** Weitere Kategorien der Veranstaltung. `category` darf mit drin stehen (Backend filtert). */
  secondaryCategories?: EventCategory[];
  eventType?: EventType;
  organizerUrl?: string;
  /** Flyer-/Bild-URL. Nicht gesetzt = Flyer des Vorschlags unverändert übernehmen. */
  flyerUrl?: string;
  fullyBooked: boolean;
  cancelled: boolean;
  tentative: boolean;
  permanent?: boolean;
  /** Saison des permanenten Events (Monate 1–12), beide leer = ganzjährig. */
  seasonFrom?: number | null;
  seasonUntil?: number | null;
  /**
   * Gebiet eines permanenten Events ohne festen Startort („Erzgebirge", „Dolomiten"). Gilt nur
   * zusammen mit `permanent` und wie die Saison: wer `permanent` mitschickt, schickt das Gebiet mit.
   */
  areaName?: string | null;
  /** Vom Reviewer korrigierter Anmeldestart. Fehlt = den des Vorschlags behalten, '' = streichen. */
  registrationStart?: string;
  registrationMode?: RegistrationMode;
  registrationState?: RegistrationState;
  distances: DistanceRequest[];
  /** Preisstufen, wie die Prüfansicht sie zeigt; fehlt = die des Vorschlags. */
  priceTiers?: EventPriceTier[] | null;
  links?: EventLink[];
}

export interface ReviewEditRequest {
  approved: boolean;
  comment?: string;
  overrides?: ReviewEditOverrides;
  /**
   * Die Redaktion hat die Warnung gesehen, dass ein neuerer Vorschlag zu diesem Event schon
   * übernommen ist, und will diesen älteren trotzdem anwenden. Ohne das Feld antwortet der
   * Server mit 409.
   */
  acknowledgeSuperseded?: boolean;
  /**
   * Ob die Favoriten-Besitzer des betroffenen Events per Mail über die übernommenen
   * Änderungen informiert werden. Setzt der Bestätigungsdialog beim Genehmigen.
   */
  notifyFavorites?: boolean;
  /**
   * Nur bei einem CREATE-Vorschlag: den Vorschlag als **nächste Auflage** dieses vorhandenen
   * Events übernehmen, statt ein zweites Event anzulegen. Das Event behält damit Vormerkungen,
   * Zahlen und seine Adresse bei Google.
   */
  applyToEventId?: number;
  /**
   * Nur beim Übernehmen: weitere offene Vorschläge, die in diesem aufgehen (Status `MERGED`).
   * Jeder Einreichende bekommt höchstens eine Mail.
   */
  mergeEditIds?: number[];
}

// Audit Log types

export interface AuditLogResponse {
  id: number;
  userId: number;
  username?: string;
  action: string;
  entityType: string;
  entityId: number;
  changes?: Record<string, unknown>;
  createdAt?: string;
}

// Enrichment queue
export type EnrichmentEntityType = 'EVENT' | 'ORGANIZER' | 'PRO_RACE' | 'PRO_RACE_STAGE';
export type EnrichmentRequestStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export interface EnqueueEnrichmentRequest {
  sourceKey: string;
  entityType: EnrichmentEntityType;
  entityId: number;
  extra?: Record<string, unknown>;
  reason?: string;
}

export interface EnrichmentRequestResponse {
  id: number;
  sourceKey: string;
  entityType: EnrichmentEntityType;
  entityId: number;
  extra?: Record<string, unknown>;
  status: EnrichmentRequestStatus;
  requestedBy?: number;
  reason?: string;
  requestedAt?: string;
  pickedUpAt?: string;
  completedAt?: string;
  error?: string;
}

// Agent enrichment state (cooldown / skip tracking)
export interface AgentStateAdminRow {
  id: number;
  entityType: string;
  entityId: number;
  displayName?: string;
  agentSource: string;
  lastRunAt?: string;
  skipUntil?: string;
  permanentSkip: boolean;
  metadata?: Record<string, unknown>;
  updatedAt?: string;
}

export interface AgentStateSummary {
  agentSource: string;
  entityType: string;
  total: number;
  permanent: number;
  activeCooldown: number;
  lastRunAt?: string;
}

// Agent observability (runs + attempts)
export interface AgentRunView {
  id: number;
  agentSource: string;
  mode?: string;
  host?: string;
  startedAt?: string;
  finishedAt?: string;
  durationSeconds?: number;
  scanned: number;
  submitted: number;
  errors: number;
  status: 'RUNNING' | 'OK' | 'FAILED';
  errorMsg?: string;
}

export interface EnrichmentAttemptView {
  id: number;
  runId?: number;
  agentSource: string;
  entityType: string;
  entityId: number;
  entityName?: string;
  status: string;
  appliedFields?: Record<string, unknown>;
  proposedFields?: Record<string, unknown>;
  sourceUrl?: string;
  confidence?: number;
  errorMsg?: string;
  createdAt?: string;
}

export interface AgentHealthRow {
  agentSource: string;
  lastRunAt?: string;
  lastSuccessAt?: string;
  staleHours?: number;
  staleThresholdBreached: boolean;
  recentRuns: number;
  recentErrors: number;
  recentErrorRate?: number;
}

export interface EntityActivityResponse {
  entityType: string;
  entityId: number;
  title?: string;
  lastActionAt: string;
  lastAction: string;
  lastActorUsername?: string;
  lastSource?: string;
  changeCount: number;
}

// Pagination

export interface PageResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// Feedback widget ("Wie gefällt dir RADEVENTS?")

export type FeedbackPlatform = 'web' | 'app';

/** Submission from the feedback widget (anonymous unless an e-mail is provided). */
export interface FeedbackSubmission {
  rating: number; // 1..5
  comment?: string;
  email?: string;
  platform: FeedbackPlatform;
}

export interface Feedback {
  id: number;
  rating: number;
  comment: string | null;
  email: string | null;
  platform: FeedbackPlatform;
  createdAt: string;
}

export interface FeedbackStats {
  total: number;
  average: number;
  withComment: number;
  counts: Record<string, number>; // rating (as string key "1".."5") → count
}

// Event Sources

export interface EventSourceResponse {
  id: number;
  sourceKey: string;
  displayName: string;
  description?: string;
  baseUrl?: string;
  priority: number;
  enabled: boolean;
  category: string; // 'SCRAPER' | 'ENRICHER'
  lastSyncAt?: string;
  lastSyncStatus?: string;
  lastSyncMessage?: string;
  lastSyncEventCount: number;
}

// Admin statistics

export interface EventOriginStat {
  sourceKey: string;
  displayName: string;
  count: number;
  percentage: number;
}

export interface EventOriginStats {
  total: number;
  origins: EventOriginStat[];
}

export interface FieldStat {
  field: string;
  count: number;
}

export interface EnrichmentCoverageStat {
  agentSource: string;
  displayName: string;
  entityType: string;
  totalEntities: number;
  enrichedAtLeastOnce: number;
  neverEnriched: number;
  coveragePercent: number;
  fields: FieldStat[];
  lastEnrichmentAt?: string;
}

export interface UpdateEventSourceRequest {
  displayName: string;
  description?: string;
  baseUrl?: string;
  priority: number;
  enabled: boolean;
}

export interface SourceSyncResult {
  sourceKey: string;
  created: number;
  enriched: number;
  skipped: number;
  errors: number;
}

// Pro Race types

export interface LinkedEventSummary {
  id: number;
  title: string;
  eventDate: string;
  city?: string;
  bundesland?: string;
  note?: string;
}

export interface ProRaceStage {
  id: number;
  proRaceId: number;
  stageNumber: number;
  stageDate?: string;
  startCity?: string;
  endCity?: string;
  distanceKm?: number;
  elevationMeters?: number;
  stageType?: string;
  winner?: string;
  description?: string;
  startTime?: string;
  estimatedArrivalTime?: string;
  hasGpx?: boolean;
  status: string;
}

export interface ProRaceStreamingProvider {
  id: number;
  proRaceId: number;
  providerName: string;
  url: string;
  note?: string;
  sortOrder: number;
  clickCount: number;
  /** true = frei empfangbar, false = Abo, null/undefined = unbekannt (kein Badge) */
  isFree?: boolean | null;
}

export interface UpsertStreamingProviderRequest {
  providerName: string;
  url: string;
  note?: string;
  sortOrder?: number;
  isFree?: boolean | null;
}

export type ProRaceClassificationType = 'GC' | 'POINTS' | 'KOM';

/**
 * One rider row of a classification leader-board ("Wertung"). Rows arrive
 * pre-sorted best→worst per type; `value` is a display string (GC leader
 * "71:24:18", others "+ 0:42", points/mountains plain integers).
 */
export interface ProRaceClassificationEntry {
  classificationType: ProRaceClassificationType;
  rank: number;
  riderName: string;
  teamName?: string;
  countryCode?: string; // ISO 3166-1 alpha-2
  value?: string;
  asOfStage?: number;
}

export interface ProRace {
  id: number;
  name: string;
  slug: string;
  raceType: string;
  category?: string;
  startDate: string;
  endDate: string;
  broadcastInfo?: string;
  description?: string;
  websiteUrl?: string;
  wikipediaUrl?: string;
  winner?: string;
  country?: string;
  totalDistanceKm?: number;
  imageUrl?: string;
  status: string;
  linkedEvents: LinkedEventSummary[];
  stages: ProRaceStage[];
  streamingProviders?: ProRaceStreamingProvider[];
  classifications?: ProRaceClassificationEntry[];
}

export interface CreateProRaceRequest {
  name: string;
  slug: string;
  raceType: string;
  category?: string;
  startDate: string;
  endDate: string;
  broadcastInfo?: string;
  description?: string;
  websiteUrl?: string;
  wikipediaUrl?: string;
  winner?: string;
  country?: string;
  totalDistanceKm?: number;
  imageUrl?: string;
  status: string;
}

export type UpdateProRaceRequest = CreateProRaceRequest;

export interface LinkEventRequest {
  eventId: number;
  note?: string;
}

export interface ProRaceEnrichmentProposal {
  id: number;
  proRaceId: number;
  proRaceName: string;
  status: string;
  proposalType: string;  // 'RACE' | 'STAGE'
  source?: string;
  // Race-level
  proposedName?: string;
  proposedDescription?: string;
  proposedBroadcastInfo?: string;
  proposedWebsiteUrl?: string;
  proposedWikipediaUrl?: string;
  proposedWinner?: string;
  proposedCategory?: string;
  proposedCountry?: string;
  proposedTotalDistanceKm?: number;
  proposedImageUrl?: string;
  // Stage-level
  stageId?: number;
  proposedStageNumber?: number;
  proposedStageDate?: string;
  proposedStartCity?: string;
  proposedEndCity?: string;
  proposedDistanceKm?: number;
  proposedElevationMeters?: number;
  proposedStageType?: string;
  // Review
  reviewerComment?: string;
  reviewedAt?: string;
  createdAt?: string;
}

export interface CreateEnrichmentProposalRequest {
  proRaceId: number;
  source?: string;
  // Race-level
  proposedName?: string;
  proposedDescription?: string;
  proposedBroadcastInfo?: string;
  proposedWebsiteUrl?: string;
  proposedWikipediaUrl?: string;
  proposedWinner?: string;
  proposedCategory?: string;
  proposedCountry?: string;
  proposedTotalDistanceKm?: number;
  proposedImageUrl?: string;
  // Stage-level (proposalType wird automatisch STAGE wenn proposedStageNumber gesetzt)
  proposedStageNumber?: number;
  proposedStageDate?: string;
  proposedStartCity?: string;
  proposedEndCity?: string;
  proposedDistanceKm?: number;
  proposedElevationMeters?: number;
  proposedStageType?: string;
}

export interface ReviewEnrichmentProposalRequest {
  approved: boolean;
  comment?: string;
}

// Address helpers

export function formatAddress(locationName?: string, street?: string, houseNumber?: string, postalCode?: string, city?: string): string {
  const parts: string[] = [];
  if (locationName) parts.push(locationName);
  if (street) parts.push(houseNumber ? `${street} ${houseNumber}` : street);
  const cityPart = postalCode ? `${postalCode} ${city}` : (city || '');
  parts.push(cityPart);
  return parts.join(', ');
}

export function buildMapsUrl(event: { locationName?: string; street?: string; houseNumber?: string; postalCode: string; city: string; bundesland: string; areaName?: string | null }): string {
  // Ohne Ort und PLZ sucht die Karte das Gebiet („Erzgebirge, Sachsen"), nicht nur die Region.
  const city = !event.city && !event.postalCode && event.areaName ? event.areaName : event.city;
  const query = formatAddress(event.locationName, event.street, event.houseNumber, event.postalCode, city) + ', ' + event.bundesland;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

// Outreach — das gemeinsame Postfach für die Ansprache von Veranstaltern und Partnern.
// Ein- und ausgehende Mails liegen im selben Verlauf; wer schreiben darf, entscheidet der Server.

export enum OutreachContactType {
  VEREIN = 'VEREIN',
  WEBSITE = 'WEBSITE',
  VERANSTALTER = 'VERANSTALTER',
  SONSTIGES = 'SONSTIGES',
}

export enum OutreachContactStatus {
  NEU = 'NEU',
  KONTAKTIERT = 'KONTAKTIERT',
  ANTWORT_ERHALTEN = 'ANTWORT_ERHALTEN',
  KOOPERATION = 'KOOPERATION',
  KEIN_INTERESSE = 'KEIN_INTERESSE',
  BOUNCE = 'BOUNCE',
}

export enum OutreachMessageDirection {
  OUT = 'OUT',
  IN = 'IN',
}

export enum OutreachMessageStatus {
  /** Eingestellt, aber noch nicht raus — der Versand läuft über eine Warteschlange. */
  QUEUED = 'QUEUED',
  SENT = 'SENT',
  FAILED = 'FAILED',
  RECEIVED = 'RECEIVED',
}

/** Was außer Mails im Verlauf steht (V171). */
export enum OutreachNoteKind {
  NOTE = 'NOTE',
  CALL_IN = 'CALL_IN',
  CALL_OUT = 'CALL_OUT',
  MEETING = 'MEETING',
}

export const OUTREACH_NOTE_KIND_LABELS: Record<OutreachNoteKind, string> = {
  [OutreachNoteKind.NOTE]: 'Notiz',
  [OutreachNoteKind.CALL_IN]: 'Anruf von ihnen',
  [OutreachNoteKind.CALL_OUT]: 'Anruf von uns',
  [OutreachNoteKind.MEETING]: 'Treffen',
};

/**
 * Worum es mit einem Kontakt geht — die Labels des Vertriebs-Boards. Gespiegelt in
 * `OutreachTopics` (Kotlin); ein neues Thema braucht beide Stellen, sonst verwirft der Server es
 * beim Speichern still. Ersetzt in der Oberfläche die Art (`contactType`).
 *
 * **Werbepartner hat zwei Unterthemen** (V177), getrennt nach dem Angebot: *Regional* ist der
 * Umkreis-Banner (Bikefitter, Radläden), *Bundesweit* die Partnerschaft mit einer Marke. Ein
 * Unterthema steht immer **zusammen mit** seinem Oberthema im Kontakt, deshalb findet jede Frage
 * nach `WERBEPARTNER` beide. Bikefitting war bis dahin ein eigenes Thema.
 */
export const OUTREACH_TOPICS: { key: string; label: string; parent?: string }[] = [
  { key: 'VERANSTALTER', label: 'Veranstalter' },
  { key: 'WERBEPARTNER', label: 'Werbepartner' },
  { key: 'WERBEPARTNER_REGIONAL', label: 'Regional', parent: 'WERBEPARTNER' },
  { key: 'WERBEPARTNER_BUNDESWEIT', label: 'Bundesweit', parent: 'WERBEPARTNER' },
  { key: 'KOOPERATION', label: 'Kooperation' },
  { key: 'TOURISMUS', label: 'Tourismus' },
];

export const outreachTopicLabel = (key: string): string =>
  OUTREACH_TOPICS.find((t) => t.key === key)?.label ?? key;

/** Die Unterthemen eines Themas, in der Reihenfolge der Liste. */
export const outreachSubtopics = (parent: string) => OUTREACH_TOPICS.filter((t) => t.parent === parent);

/**
 * Die Themen eines Kontakts zum Lesen: ein Oberthema mit Unterthema wird zu einer Angabe
 * („Werbepartner · Regional“), damit die Zeile nicht zweimal Werbepartner sagt.
 */
export const outreachTopicLabels = (topics: string[] | undefined): string[] => {
  const t = topics ?? [];
  return OUTREACH_TOPICS
    .filter((d) => !d.parent && t.indexOf(d.key) >= 0)
    .map((d) => {
      const sub = outreachSubtopics(d.key).filter((c) => t.indexOf(c.key) >= 0).map((c) => c.label);
      return sub.length ? `${d.label} · ${sub.join(', ')}` : d.label;
    });
};

/**
 * Ein Thema an- oder abwählen, wie das Formular es braucht: ein Unterthema bringt sein Oberthema
 * mit und schließt die anderen Unterthemen aus, ein abgewähltes Oberthema nimmt seine
 * Unterthemen mit. Dieselbe Regel setzt `OutreachTopics.normalize` auf dem Server durch.
 */
export const toggleOutreachTopic = (topics: string[], key: string): string[] => {
  const def = OUTREACH_TOPICS.find((t) => t.key === key);
  if (!def) return topics;
  if (topics.indexOf(key) >= 0) {
    return topics.filter((t) => t !== key && OUTREACH_TOPICS.find((d) => d.key === t)?.parent !== key);
  }
  if (def.parent) {
    const siblings = outreachSubtopics(def.parent).map((c) => c.key);
    const rest = topics.filter((t) => siblings.indexOf(t) < 0);
    return rest.indexOf(def.parent) >= 0 ? [...rest, key] : [...rest, def.parent, key];
  }
  return [...topics, key];
};

/**
 * Worauf ein Entwurf des Schreib-Agenten hinaus will (V173). Gespiegelt in `OutreachDraftGoal`
 * (Kotlin); das Ziel wählt Angebot und Zahlen im Briefing.
 */
export type OutreachDraftGoal = 'WERBEPLATZ' | 'ZUGANG' | 'PREMIUM' | 'FREI';

export const OUTREACH_DRAFT_GOALS: { key: OutreachDraftGoal; label: string; hint: string }[] = [
  { key: 'WERBEPLATZ', label: 'Werbeplatz', hint: 'Banner im Umkreis von 80 km, 99 € im Monat' },
  { key: 'ZUGANG', label: 'Zugang', hint: 'Verein übernimmt seine Seite, kostenlos' },
  { key: 'PREMIUM', label: 'Premium', hint: 'Premium oder Hervorhebung für einen Verein mit Zugang' },
  { key: 'FREI', label: 'Frei', hint: 'Ohne festes Angebot, die Anweisung sagt, worum es geht' },
];

export const outreachDraftGoalLabel = (key: OutreachDraftGoal): string =>
  OUTREACH_DRAFT_GOALS.find((g) => g.key === key)?.label ?? key;

/** Ziel ohne Auswahl, wie der Server es aus den Themen ableitet (`OutreachDraftGoal.fromTopics`). */
export const defaultDraftGoal = (topics: string[] | undefined, organizerId?: number | null): OutreachDraftGoal => {
  const t = topics ?? [];
  // Bundesweit ist eine Partnerschaft, kein Umkreis-Banner — der Werbeplatz wäre das falsche Angebot.
  if (t.indexOf('WERBEPARTNER_BUNDESWEIT') >= 0) return 'FREI';
  if (t.indexOf('WERBEPARTNER') >= 0) return 'WERBEPLATZ';
  if (t.indexOf('VERANSTALTER') >= 0 || organizerId != null) return 'ZUGANG';
  return 'FREI';
};

export type OutreachDraftStatus = 'REQUESTED' | 'READY' | 'FAILED' | 'USED' | 'DISCARDED';

export interface OutreachDraftObservation {
  text: string;
  url?: string | null;
}

/** Die Zahlen, wie sie beim Bestellen galten. Die `*Text`-Felder sind fertig gerundet. */
export interface OutreachDraftFacts {
  location?: { label: string; latitude: number; longitude: number; source: 'CONTACT' | 'ORGANIZER' } | null;
  reach?: {
    radiusKm: number;
    windowDays: number;
    /** Alle veröffentlichten Events im Umkreis, auch gelaufene; der Name ist älter als die Regel. */
    upcomingEvents: number;
    views: number;
    usable: boolean;
    upcomingEventsText: string;
    viewsText: string;
    bannerImpressionsPerYearText?: string | null;
  } | null;
  organizer?: { id: number; name: string; reachSentence: string } | null;
}

export interface OutreachDraft {
  id: number;
  contactId: number;
  status: OutreachDraftStatus;
  goal: OutreachDraftGoal;
  instructions?: string | null;
  facts?: OutreachDraftFacts | null;
  subject?: string | null;
  body?: string | null;
  observations: OutreachDraftObservation[];
  agentNotes?: string | null;
  error?: string | null;
  usedMessageId?: number | null;
  createdAt: string;
  completedAt?: string | null;
}

export interface OutreachDraftRequest {
  /** Leer = aus den Themen des Kontakts. */
  goal?: OutreachDraftGoal;
  instructions?: string;
  postalCode?: string;
  city?: string;
}

/** Eine Notiz oder ein Anruf im Verlauf. `occurredAt` ist, wann es war, nicht wann es eingetragen wurde. */
export interface OutreachNote {
  id: number;
  contactId: number;
  kind: OutreachNoteKind;
  body: string;
  occurredAt: string;
  createdByUserId?: number;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OutreachNoteRequest {
  kind: OutreachNoteKind;
  body: string;
  /** ISO-Zeitpunkt; leer = jetzt. */
  occurredAt?: string;
}

/** Jemand aus der Redaktion — für „Zuständig“. */
export interface OutreachTeamMember {
  id: number;
  name: string;
  firstName?: string | null;
}

export enum OutreachSuppressionReason {
  UNSUBSCRIBED = 'UNSUBSCRIBED',
  BOUNCE = 'BOUNCE',
  COMPLAINT = 'COMPLAINT',
  MANUAL = 'MANUAL',
}

export interface OutreachContact {
  id: number;
  name: string;
  email: string;
  organization?: string;
  website?: string;
  contactType: OutreachContactType;
  status: OutreachContactStatus;
  notes?: string;
  /** Schlüssel aus `OUTREACH_TOPICS`. */
  topics?: string[];
  /** Wer den Kontakt hält — nicht, wer zuletzt geschrieben hat. */
  ownerUserId?: number | null;
  /** Der Veranstalter, um den es geht — leer bei Webseiten und freien Partnern. */
  organizerId?: number;
  /** Standort für die Umkreiszahlen der Entwürfe. `located`: die Adresse wurde gefunden. */
  postalCode?: string | null;
  city?: string | null;
  located?: boolean;
  lastContactedAt?: string;
  /**
   * Der Verlaufsstand — nur in der Liste gefüllt. Er unterscheidet den **Chat** vom bloßen
   * Adressbuch-Eintrag: `lastContactedAt` taugt dafür nicht, das setzt allein der erfolgreiche
   * Versand (ein aus einer Anfrage übernommener Kontakt hat eine eingegangene Nachricht und
   * trotzdem kein `lastContactedAt`).
   */
  messageCount?: number;
  lastMessageAt?: string;
  lastMessageDirection?: OutreachMessageDirection;
  lastMessageSnippet?: string;
  /** Vorname dessen, der die letzte Nachricht geschrieben hat — nur bei ausgehenden. */
  lastMessageAuthor?: string;
  /** Ist der jüngste Eintrag eine Notiz oder ein Anruf, dessen Art. */
  lastNoteKind?: OutreachNoteKind | null;
  /** Notizen und Anrufe — zusammen mit `messageCount` macht das einen Kontakt zum Chat. */
  noteCount?: number;
  /**
   * Steht in der Badge: die letzte Mail kam von außen und ist nicht als gelesen markiert, oder
   * der Chat wurde von Hand als ungelesen markiert. Nur in der Liste gefüllt.
   */
  unread?: boolean;
  createdAt: string;
  updatedAt: string;
  createdByUserId?: number;
}

/** Ein Kontakt samt der Frage, ob ihm gerade geschrieben werden darf. */
export interface OutreachContactDetail {
  contact: OutreachContact;
  /** Null heißt: darf. Sonst der Satz, der in der Oberfläche steht. */
  blockedReason?: string | null;
}

/**
 * Was im Adressbuch anders steht als beim verknüpften Veranstalter. Zwei Datensätze mit Absicht:
 * der Kontakt ist der *Ansprechpartner*, der Veranstalter der Verein — eine im Postfach
 * korrigierte Adresse ändert deshalb nichts am Verzeichnis.
 */
export interface OutreachOrganizerDiff {
  organizerId: number;
  organizerName: string;
  /** Leer heißt: es gibt nichts zu übernehmen. */
  fields: OutreachOrganizerDiffField[];
}

export interface OutreachOrganizerDiffField {
  /** `contactPerson` | `email` | `website` — der Vereinsname ist bewusst nicht dabei. */
  key: string;
  label: string;
  contactValue: string;
  organizerValue?: string;
}

export interface OutreachAttachment {
  id: number;
  fileId: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

/** Woher die Outreach-Mails kommen und ob der Eingang abgeholt wird. */
export interface OutreachMailbox {
  /** Die Adresse, unter der geschrieben wird und an die Antworten gehen. */
  address?: string;
  /** `false` = Antworten muss jemand von Hand im Postfach nachsehen. */
  inboxActive: boolean;
}

/** Eine hochgeladene Datei, bevor sie an einer Nachricht hängt. */
export interface OutreachFile {
  id: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

export interface OutreachMessage {
  id: number;
  /** Leer bei einer eingegangenen Mail, die sich keinem Kontakt zuordnen ließ. */
  contactId?: number;
  direction: OutreachMessageDirection;
  templateId?: number;
  subject: string;
  body: string;
  status: OutreachMessageStatus;
  errorMessage?: string;
  fromEmail?: string;
  toEmail?: string;
  occurredAt: string;
  sentByUserId?: number;
  /** Wer geschrieben hat — der Zweck der ganzen Ansicht. */
  sentByUsername?: string;
  /** Vor- und Nachname dazu; fehlt, wenn am Konto keiner steht. */
  sentByName?: string;
  /**
   * Gesetzt, wenn die Redaktion einen unzuordenbaren Eingang zur Seite gelegt hat (Spam,
   * Provider-Nachricht). Er zählt dann nicht mehr in der Badge, bleibt aber lesbar.
   */
  dismissedAt?: string;
  /** Aus dem eigenen Mailprogramm nachgetragen, nie über dieses Postfach gelaufen. */
  imported?: boolean;
  /** Die Karte, die unter der Mail stand. */
  signature?: MailSignatureCard | null;
  attachments: OutreachAttachment[];
}

/**
 * Die Signatur-Karte unter einer Mail aus dem Dashboard (V172): Name aus dem Konto, Marke und
 * Webadresse fest, dazu Funktion, Telefon und Adresse. An jeder Nachricht als Momentaufnahme.
 */
export interface MailSignatureCard {
  name: string;
  role?: string | null;
  phone?: string | null;
  /** Leer: auf der Karte steht die Adresse des Postfachs, über das die Mail rausgeht. */
  email?: string | null;
  web: string;
}

/** Die eigene Signatur: gepflegte Angaben und die Karte, die daraus wird. */
export interface MailSignature {
  role?: string | null;
  phone?: string | null;
  email?: string | null;
  /** Fehlt, wenn am Konto kein Name steht. */
  card?: MailSignatureCard | null;
  senderName?: string | null;
}

export interface MailSignatureRequest {
  role?: string;
  phone?: string;
  email?: string;
}

export interface OutreachTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  isDefault: boolean;
  /** Fest an der Vorlage hängende Dateien — der Flyer. */
  attachments: OutreachAttachment[];
  createdAt: string;
  updatedAt: string;
}

export interface OutreachSuppression {
  email: string;
  reason: OutreachSuppressionReason;
  note?: string;
  createdByUserId?: number;
  createdAt: string;
}

export interface OutreachPlaceholder {
  key: string;
  description: string;
  /**
   * Was beim Versand an dieser Stelle steht — die eigentliche Erklärung.
   *
   * Eine **Liste**, weil die Satzbauer (`veranstaltung`, `reichweite`) je nach Datenlage etwas
   * anderes einsetzen und einer der Fälle „gar nichts" ist. An einem einzigen Beispiel wäre das
   * nicht zu erkennen.
   */
  examples: string[];
}

export interface CreateOutreachContactRequest {
  name: string;
  email: string;
  organization?: string;
  website?: string;
  contactType: OutreachContactType;
  organizerId?: number;
  notes?: string;
  topics?: string[];
}

export interface UpdateOutreachContactRequest {
  name: string;
  email: string;
  organization?: string;
  website?: string;
  contactType: OutreachContactType;
  status: OutreachContactStatus;
  organizerId?: number;
  notes?: string;
  /** Weglassen = unverändert. */
  topics?: string[];
}

export interface SendOutreachEmailRequest {
  subject: string;
  body: string;
  templateId?: number;
  attachmentFileIds?: number[];
  /** Der Entwurf, aus dem die Mail entstand — Entwurf und fertige Fassung werden zum Stilbeispiel. */
  draftId?: number;
  /** An wen die Antwort geht — eine Adresse, die im Verlauf geschrieben hat; leer: die im Adressbuch. */
  toEmail?: string;
}

export interface InviteOrganizersRequest {
  organizerIds: number[];
  templateId: number;
  attachmentFileIds?: number[];
  /**
   * Vereine, die schon einen Verwalter haben, überspringen.
   *
   * Nur beim Ziel „Zugang holen" richtig: wer Hervorhebung oder Premium verkauft, meint gerade
   * die betreuten Vereine. Fehlt das Feld, überspringt der Server sie — wie vor der Zielauswahl.
   */
  skipWithMembers?: boolean;
}

export interface InviteOrganizersResult {
  queued: number;
  /** Je übersprungenem Verein ein Satz, warum — sonst bleibt unklar, wen es nicht getroffen hat. */
  skipped: string[];
}

export interface CreateOutreachTemplateRequest {
  name: string;
  subject: string;
  body: string;
  isDefault?: boolean;
}

export interface UpdateOutreachTemplateRequest {
  name: string;
  subject: string;
  body: string;
  isDefault?: boolean;
}

export const OUTREACH_CONTACT_TYPE_LABELS: Record<OutreachContactType, string> = {
  [OutreachContactType.VEREIN]: 'Verein',
  [OutreachContactType.WEBSITE]: 'Website',
  [OutreachContactType.VERANSTALTER]: 'Veranstalter',
  [OutreachContactType.SONSTIGES]: 'Sonstiges',
};

export const OUTREACH_CONTACT_STATUS_LABELS: Record<OutreachContactStatus, string> = {
  [OutreachContactStatus.NEU]: 'Neu',
  [OutreachContactStatus.KONTAKTIERT]: 'Kontaktiert',
  [OutreachContactStatus.ANTWORT_ERHALTEN]: 'Antwort erhalten',
  [OutreachContactStatus.KOOPERATION]: 'Kooperation',
  [OutreachContactStatus.KEIN_INTERESSE]: 'Kein Interesse',
  [OutreachContactStatus.BOUNCE]: 'Unzustellbar',
};

export const OUTREACH_SUPPRESSION_REASON_LABELS: Record<OutreachSuppressionReason, string> = {
  [OutreachSuppressionReason.UNSUBSCRIBED]: 'Hat sich abgemeldet',
  [OutreachSuppressionReason.BOUNCE]: 'Unzustellbar',
  [OutreachSuppressionReason.COMPLAINT]: 'Beschwerde',
  [OutreachSuppressionReason.MANUAL]: 'Von Hand gesperrt',
};

// ==================== News ====================

export enum NewsCategory {
  ROAD = 'ROAD',
  GRAVEL = 'GRAVEL',
  MTB = 'MTB',
  SZENE = 'SZENE'
}

export enum NewsStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED'
}

/** Kompakte Info zum optional verknüpften Event (für Karte/CTA im News-Artikel). */
export interface NewsLinkedEvent {
  id: number;
  title: string;
  eventDate: string;
  endDate?: string | null;
  city: string;
  bundesland: string;
  category: EventCategory;
  status: EventStatus;
}

export interface NewsPost {
  id: number;
  slug: string;
  title: string;
  /**
   * Redaktioneller Anriss. Optional: fehlt er, leitet die Anzeige einen Teaser aus dem
   * Fließtext ab (Liste server-seitig, Detail/SEO über `newsTeaser`). In der Detail-/Admin-
   * Antwort steht der rohe Wert (ggf. null), damit der Editor keinen abgeleiteten Text speichert.
   */
  teaser: string | null;
  /** Fließtext; Absätze durch Leerzeilen getrennt. */
  content: string;
  coverImageUrl?: string | null;
  photoCredit?: string | null;
  category: NewsCategory;
  status: NewsStatus;
  /** Fließtext im Blocksatz ausgeben (nur auf breiten Viewports). */
  justified?: boolean;
  /** ISO-Datum (yyyy-MM-dd) der Veröffentlichung; null solange DRAFT. */
  publishedAt?: string | null;
  authorName: string;
  /**
   * Verknüpfte Events: IDs + kompakte Infos (letztere nur, soweit die Events sichtbar sind).
   * Mehrere sind der Normalfall — eine reale Veranstaltung liegt bei uns oft als Rennrad-
   * **und** Gravel-Event vor, ein Bericht darüber gehört an beide.
   */
  linkedEventIds?: number[];
  linkedEvents?: NewsLinkedEvent[];
  createdAt?: string;
  updatedAt?: string;
}

/** Listenansicht ohne Fließtext — GET /news liefert den content nur auf der Detailroute. */
export type NewsPostSummary = Omit<NewsPost, 'content'>;

export interface CreateNewsPostRequest {
  slug?: string;
  title: string;
  /** Optional — leer/weggelassen leitet die Anzeige den Teaser aus dem Fließtext ab. */
  teaser?: string | null;
  content: string;
  coverImageUrl?: string | null;
  photoCredit?: string | null;
  category: NewsCategory;
  authorName?: string;
  justified?: boolean;
  /** Optional: IDs bestehender Events, mit denen der Artikel verknüpft wird. */
  linkedEventIds?: number[];
}

export interface UpdateNewsPostRequest {
  slug: string;
  title: string;
  /** Optional — leer/weggelassen leitet die Anzeige den Teaser aus dem Fließtext ab. */
  teaser?: string | null;
  content: string;
  coverImageUrl?: string | null;
  photoCredit?: string | null;
  category: NewsCategory;
  authorName?: string;
  justified?: boolean;
  /**
   * IDs der verknüpften Events. Weglassen heißt „unverändert", eine leere Liste löst alle
   * Verknüpfungen.
   */
  linkedEventIds?: number[];
}

/** Bild-Vorschlag eines Enricher-Agents, wartet auf Freigabe durch Moderator/Admin. */
export interface EventImageProposal {
  id: number;
  eventId: number;
  eventTitle: string;
  eventDate: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  /** Agent-/Modellname — ohne Herkunft ist der Vorschlag nicht beurteilbar. */
  source?: string;
  prompt?: string;
  aiGenerated: boolean;
  credit?: string;
  /** Vorschau-URL (nur fuer Mods/Admins abrufbar). */
  imageUrl: string;
  /** Das Event hat bereits ein Bild — die Freigabe wuerde es ersetzen. */
  eventHasImage: boolean;
  reviewerNote?: string;
  reviewedAt?: string;
  createdAt?: string;
}

/** Build-Identitaet der laufenden Backend-Instanz (GET /api/v1/meta/version). */
export interface BuildInfo {
  /** Release-Marke, aktuell "dev" bis CalVer-Tags gesetzt werden. */
  version: string;
  /** Deployter Commit; "unknown", wenn der Build ihn nicht mitbekommen hat. */
  commitSha: string;
  commitShaShort: string;
  /** Start der Instanz — verraet, wann der letzte Deploy wirklich durchlief. */
  startedAt: string;
  /** Hoechste angewandte Flyway-Version; nur fuer Admins gefuellt. */
  dbSchemaVersion?: string | null;
}

/** Zielgruppe einer Neuerung: redaktionell formuliert vs. technischer Rest. */
export enum ReleaseNoteAudience {
  MODERATOR = 'MODERATOR',
  ADMIN = 'ADMIN',
}

export interface ReleaseNoteView {
  id: number;
  commitSha: string;
  audience: ReleaseNoteAudience;
  text: string;
  /** Seit dem letzten Besuch der Neuerungen-Seite dazugekommen. */
  unread: boolean;
}

/** Alle Notizen einer Veroeffentlichung — die Ansicht gruppiert nach Deploy, nicht nach Commit. */
export interface ReleaseNoteGroup {
  deploySha: string;
  /** CalVer-Marke des Deploys, z. B. "2026.07.30". */
  version?: string | null;
  deployedAt?: string | null;
  notes: ReleaseNoteView[];
}

export interface ReleaseNotesResponse {
  unreadCount: number;
  seenAt?: string | null;
  deploys: ReleaseNoteGroup[];
}

// ── Geschäftszahlen (/admin/geschaeft) ──────────────────────────────────────────────────────

/**
 * Die Antwort von `GET /api/v1/admin/business-stats` — die Fragen aus Abschnitt 14 des
 * Business-Audits, in der Reihenfolge, in der die Seite sie zeigt.
 *
 * Gerechnet wird im Backend, das Frontend formatiert nur. Jeder Block trägt selbst, seit wann
 * er etwas weiß: eine Null ohne Anfangsdatum unterscheidet nicht zwischen „niemand kam" und
 * „noch nicht gemessen".
 */
export interface BusinessStats {
  days: number;
  from: string;
  to: string;
  funnel: BusinessFunnel;
  organizers: BusinessOrganizers;
  registrationDomains: RegistrationDomains;
  outreach: BusinessOutreach;
  mail: BusinessMail;
  advertising: BusinessAdvertising;
  /** null = die Besucherstatistik ist nicht eingerichtet oder hat für den Zeitraum nichts. */
  visitors: BusinessVisitors | null;
  /** Partnerprogramme (CJ für Booking.com, Awin für Delta Bike). */
  affiliate: AffiliateBlock;
}

/**
 * Partnerprogramme: unsere eigenen Klicks auf die Partnerlinks neben dem, was das Netzwerk an
 * Klicks, Verkäufen und Provision meldet. Beträge in Cent, gerechnet im Backend
 * (`AffiliateStatsService`), eingesammelt nachts (`AffiliateCollector`).
 */
export interface AffiliateBlock {
  from: string;
  to: string;
  networks: AffiliateNetworkState[];
  partners: AffiliatePartnerStat[];
  /** Events, deren Partnerlink Provision gebracht hat (Unterkennung `event-<id>`). */
  events: AffiliateEventStat[];
  /** Erster gemeldeter Verkauf; `null` = noch keiner. */
  since: string | null;
}

export interface AffiliateNetworkState {
  network: 'cj' | 'awin';
  label: string;
  configured: boolean;
  status: 'OK' | 'EMPTY' | 'ERROR' | 'DISABLED' | null;
  message: string | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
}

export interface AffiliatePartnerStat {
  network: 'cj' | 'awin';
  advertiserId: string | null;
  name: string;
  /** Klicks aus unserem Tracking; `null`, wenn wir den Link nicht selbst zählen. */
  ownClicks: number | null;
  /** Klicks laut Netzwerk; `null`, wenn es keine herausgibt (CJ). */
  networkClicks: number | null;
  sales: number;
  saleCents: number;
  commissionPendingCents: number;
  commissionConfirmedCents: number;
  commissionDeclinedCents: number;
  currency: string;
}

export interface AffiliateEventStat {
  eventId: number;
  title: string | null;
  sales: number;
  commissionCents: number;
  currency: string;
}

/**
 * Push „Neue Provision" für das eigene Konto (`/admin/business-stats/affiliate/push`).
 * `devices` = angemeldete Telefone; ohne eins klingelt nichts, auch wenn der Schalter an ist.
 */
export interface AffiliatePushState {
  enabled: boolean;
  devices: number;
}

/** Die Kachel „Partnerprogramme" auf der Übersicht, nur für ADMIN. */
export interface AdminStartAffiliate {
  days: number;
  /** Summe offen + bestätigt; `null` bei verschiedenen Währungen. */
  commissionCents: number | null;
  currency: string;
  /** Mindestens ein Netzwerk hat einen Token. */
  configured: boolean;
  partners: AffiliatePartnerStat[];
}

/**
 * Der Werbeplatz über der Eventliste: was ausgespielt wurde und was daraus an Anfragen kam.
 * Die Slot-Ids sind die aus `PromoBannerSlot` (`organizer-promo`, `partner-promo`).
 */
export interface BusinessAdvertising {
  /** Erste gezählte Einblendung. null = es lief noch keine Anzeige. */
  since: string | null;
  slots: BusinessAdSlot[];
  /** Partner-Anfragen im Zeitraum, ohne Spamverdacht. */
  inquiries: number;
  /** Davon über die Haus-Anzeige (`/fuer-partner?via=banner`). */
  inquiriesFromBanner: number;
  /** Erste Partner-Anfrage überhaupt. */
  inquirySince: string | null;
}

export interface BusinessAdSlot {
  slot: string;
  impressions: number;
  clicks: number;
  visitors: number;
  clickers: number;
  /** Klicks je 100 Einblendungen. null ohne Einblendung. */
  clickRate: number | null;
}

export interface BusinessFunnel {
  since: string | null;
  steps: BusinessFunnelStep[];
  bookings: BusinessBookingStage[];
  /** Anteil derer, die nach dem Angebot etwas bestellen (Premium oder Hervorhebung), 0..100. */
  orderShare: number | null;
  /** Die Stufe mit dem größten Verlust gegenüber ihrem Vorgänger. */
  weakestStep: string | null;
  /** Erster Schritt mit Herkunft. null = seit der Umstellung wurde noch keiner gezählt. */
  originSince: string | null;
  /** Benannte Eingänge zur Angebotsseite (`?via=banner`), absteigend nach Personen. */
  entries: BusinessFunnelEntry[];
  /** Erster Aufruf der Angebotsseite mit benanntem Eingang. null = noch keiner. */
  entrySince: string | null;
  paidCents: number;
  awaitingPaymentCents: number;
}

export interface BusinessFunnelEntry {
  /** `banner` = der Banner über der Eventliste. */
  entry: string;
  people: number;
  /** Anteil an allen Besuchern der Angebotsseite, 0..100. */
  shareOfOfferViews: number | null;
}

export interface BusinessFunnelStep {
  step: string;
  /** Personen, die am selben Tag zuvor auf der Angebotsseite waren. */
  viaOffer: number;
  /** Personen, die ohne die Angebotsseite eingestiegen sind: Mail, Event-Seite, Portal. */
  direct: number;
  /** Personen aus der Zeit vor der Herkunfts-Messung. */
  unknownOrigin: number;
  /** Die Stufe, aus der diese hervorgeht. Der Weg verzweigt, es ist nicht die Zeile darüber. */
  parent: string | null;
  people: number;
  events: number;
  shareOfFirst: number | null;
  /** Anteil an [parent], 0..100. */
  shareOfPrevious: number | null;
}

export interface BusinessBookingStage {
  stage: string;
  count: number;
  amountCents: number;
}

export interface BusinessOrganizers {
  kinds: BusinessOrganizerKind[];
  top: BusinessTopOrganizer[];
}

export interface BusinessOrganizerKind {
  kind: OrganizerKind;
  organizers: number;
  events: number;
  withPremium: number;
  withManager: number;
  views: number;
}

export interface BusinessTopOrganizer {
  id: number;
  name: string;
  kind: OrganizerKind;
  premium: boolean;
  managers: number;
  events: number;
  views: number;
  visitors: number;
  outboundClicks: number;
  lastOutreachAt: string | null;
}

export interface RegistrationDomains {
  since: string | null;
  rows: RegistrationDomainRow[];
  /** Monate als `yyyy-MM`; jede Zeile trägt ihre Werte in dieser Reihenfolge. */
  months: string[];
  withoutDomain: number;
}

export interface RegistrationDomainRow {
  domain: string;
  clicks: number;
  visitors: number;
  events: number;
  byMonth: number[];
}

export interface BusinessOutreach {
  contacted: number;
  delivered: number;
  failed: number;
  replied: number;
  replyRate: number | null;
  medianHoursToReply: number | null;
  unsubscribed: number;
  byTemplate: OutreachTemplateStat[];
  recent: OutreachRecentMessage[];
}

export interface OutreachTemplateStat {
  templateId: number | null;
  name: string | null;
  contacted: number;
  replied: number;
  replyRate: number | null;
}

export interface OutreachRecentMessage {
  contactId: number | null;
  name: string | null;
  email: string | null;
  organizerId: number | null;
  subject: string;
  sentAt: string;
  status: string;
  repliedAt: string | null;
}

export interface BusinessMail {
  subscribers: MailSubscriberStat[];
  sends: MailSendStat[];
  confirmedAccounts: number;
  accounts: number;
}

export interface MailSubscriberStat {
  type: string;
  subscribers: number;
  confirmed: number;
  premiumOnly: boolean;
}

export interface MailSendStat {
  mailType: string;
  sent: number;
  failed: number;
}

export interface BusinessVisitors {
  days: number;
  visitors: number;
  visits: number;
  pageviews: number;
  visitsPerVisitor: number;
  pagesPerVisit: number;
  avgSecondsPerVisit: number;
  multiPageShare: number;
}

// ── Suchmaschinen-Auswertung: Event-Suchbegriffe gegen das Vorjahr ──────────

/** Ein Event-Suchbegriff gegen denselben Begriff im Vorjahr, jeweils um den eigenen Renntag. */
export interface YearOverYearRow {
  query: string;
  previousQuery: string;
  eventId: number;
  eventTitle: string;
  raceDay: string;
  previousRaceDay: string;
  /** Vorjahrestermin nicht gespeichert, sondern um ein Jahr zurückgerechnet. */
  previousRaceDayEstimated: boolean;
  from: string;
  to: string;
  previousFrom: string;
  previousTo: string;
  /** Falsch, solange das Fenster noch läuft — das Vorjahr ist dann gleich lang gekürzt. */
  windowComplete: boolean;
  clicks: number;
  previousClicks: number;
  clickDelta: number;
  impressions: number;
  previousImpressions: number;
  position: number | null;
  previousPosition: number | null;
}

export interface YearOverYearReport {
  /** Ab wann Zahlen je Suchbegriff gespeichert sind. */
  dataFrom: string | null;
  dataTo: string | null;
  daysBefore: number;
  daysAfter: number;
  winners: YearOverYearRow[];
  losers: YearOverYearRow[];
  /** Events, deren Vorjahresfenster vor dem Beginn der Speicherung liegt. */
  eventsWithoutPreviousData: number;
}

/**
 * Schalter einer Anzeige im Werbeplatz über der Eventliste (`/admin/werbeplatz`, V180). Der
 * Schlüssel ist zugleich die Slot-Id der Banner-Messung; die Anzeige selbst ist eine Komponente
 * im Frontend (`components/adBanners.tsx`).
 */
export interface AdBannerSetting {
  key: string;
  active: boolean;
  /** Ankündigung oder Werbung (V194); fehlt bei einem älteren Backend und gilt dann als Werbung. */
  kind?: AdBannerKind;
  updatedAt?: string | null;
  /** Benutzername der letzten Umschaltung; fehlt bei der Voreinstellung aus der Migration. */
  updatedBy?: string | null;
}

/**
 * Ankündigung (`ANNOUNCEMENT`, steht über den Events) oder Werbung (`AD`, steht zwischen den
 * Events; ohne laufende Ankündigung am Desktop oben). Die Redaktion stellt sie je Anzeige ein.
 */
export type AdBannerKind = 'ANNOUNCEMENT' | 'AD';

/** Antwort von `GET /api/v1/public/ad-banners/active`. */
export interface ActiveAdBanner {
  key: string;
  kind: AdBannerKind;
}

/** Antwort von `GET /api/v1/admin/ad-banners/stats`: der Werbeplatz für einen Zeitraum. */
export interface AdBannerStatsResponse {
  days: number;
  from: string;
  to: string;
  advertising: BusinessAdvertising;
}

/**
 * Antwort von `GET /api/v1/admin/start`: die Startseite des Verwaltungsbereichs. Je Block die
 * eine Zahl, auf die man zuerst schaut; gerechnet im Backend (`AdminStartService`).
 */
export interface AdminStart {
  feedback: AdminStartFeedback;
  organizerManagers: AdminStartOrganizerManagers;
  /** `null`, solange keine Besucherzahlen gesammelt sind. */
  visitors: AdminStartVisitors | null;
  topEvents: AdminStartTopEvents;
  upcoming: AdminStartUpcoming;
  users: AdminStartUsers;
  /** Freunde (seit 28.10.2026): wie viele sich schon verbunden haben. */
  friends: AdminStartFriends;
  /** Nur für ADMIN, sonst `null` (Umsatz gehört zu den Geschäftszahlen). */
  affiliate?: AdminStartAffiliate | null;
}

export interface AdminStartFeedback {
  /** Alle Bewertungen seit Beginn und ihr Durchschnitt (1–5); `null` ohne Bewertung. */
  total: number;
  average: number | null;
  last7Days: number;
  /** Höchste Id insgesamt; verglichen mit der zuletzt unter „Nutzer-Feedback" gesehenen. */
  newestId: number | null;
  /** Neuer als die zuletzt gelesene Id; `null`, wenn das Konto noch nichts gelesen hat. */
  unseen: number | null;
  latest: AdminStartFeedbackEntry[];
}

export interface AdminStartFeedbackEntry {
  id: number;
  rating: number;
  comment: string | null;
  platform: FeedbackPlatform;
  createdAt: string;
}

export interface AdminStartOrganizerManagers {
  /** Veranstalter mit mindestens einem aktiven Verwalter-Konto. */
  total: number;
  newLast7Days: number;
  newLast30Days: number;
  pendingRequests: number;
}

export interface AdminStartVisitors {
  days: number;
  from: string;
  to: string;
  visitors: number;
  /** `null`: in der Vorperiode wurde noch nicht gemessen. */
  previousVisitors: number | null;
  previousFrom: string;
  previousTo: string;
  /** Falsch, wenn die Vorperiode vor dem Messbruch liegt. */
  comparable: boolean;
}

export interface AdminStartTopEvents {
  days: number;
  /** Ein Merken zählt so viel wie diese Zahl Klicks. */
  favoriteWeight: number;
  /** Alle gefragten Events mit fehlenden Angaben; `events` ist der Anfang davon. */
  total: number;
  events: AdminStartTopEvent[];
}

/** Beliebte oder verhältnismäßig beliebte Events der nächsten `days` Tage mit fehlenden Angaben. */
export interface AdminStartUpcoming {
  days: number;
  popularMinFavorites: number;
  popularMinParticipants: number;
  total: number;
  events: AdminStartUpcomingEvent[];
}

export interface AdminStartUpcomingEvent {
  id: number;
  title: string;
  eventDate: string;
  city: string | null;
  /** Alle Merken, nicht nur die neuen. */
  favorites: number;
  participants: number | null;
  /** Erfüllt die Badge-Regel „Beliebt"; sonst nur im Vergleich zum Zeitfenster gefragt. */
  popular: boolean;
  scoreBreakdown: ScoreComponent[];
}

export interface AdminStartTopEvent {
  id: number;
  title: string;
  eventDate: string;
  permanent: boolean;
  city: string | null;
  clicks: number;
  favorites: number;
  scoreBreakdown: ScoreComponent[];
}

export interface AdminStartFriends {
  /** Bestätigte Freundschaften (Paare). */
  friendships: number;
  /** Offene Anfragen. */
  pending: number;
  /** Konten mit mindestens einem bestätigten Freund. */
  accountsWithFriends: number;
  /** Nach dem Datum der Anfrage; ein eigenes Annahmedatum gibt es nicht. */
  newLast7Days: number;
}

export interface AdminStartUsers {
  total: number;
  newToday: number;
  newYesterday: number;
  newLast7Days: number;
}

// ── Winterchallenge (docs/bereiche/winterchallenge.md) ─────────────────────────────────────────

/** Womit gefahren wurde. Eigener Satz statt `EventCategory`, weil Indoor keine Event-Kategorie ist. */
export type ChallengeBikeType = 'ROAD' | 'GRAVEL' | 'MTB' | 'TOUR' | 'INDOOR';

/** Ein Eintrag „Kilometer gefahren“. Der Nachweis-Link sieht nur die Redaktion. */
export interface ChallengeRide {
  id: number;
  /** ISO-Datum, im Wertungszeitraum und nicht in der Zukunft. */
  rideDate: string;
  km: number;
  bikeType: ChallengeBikeType;
  /** Optional: Link auf Strava, Garmin, Komoot … zur Prüfung im Zweifel. */
  proofUrl?: string | null;
}

/** Der Saison-Kopf, für alle gleich und öffentlich. */
export interface ChallengeSummary {
  slug: string;
  /** „Winterchallenge 2026/27“ */
  title: string;
  startsOn: string;
  endsOn: string;
  goalKm: number;
  totalKm: number;
  participants: number;
  teams: number;
  countries: number;
  sponsor?: { name: string; url: string; logoUrl?: string | null } | null;
  /**
   * Läuft gerade ein Profi-Rennen oder beginnt eins in den nächsten 14 Tagen? Entscheidet der
   * Server, damit das Menü nicht die ganze Rennliste lädt und zweimal springt. Fehlt bei einem
   * älteren Backend und gilt dann als „ja“ (Profi-Rennen bleibt stehen).
   */
  proRacesActive?: boolean;
}

/** Was ein angemeldeter Teilnehmer über sich selbst sieht. */
export interface ChallengeMe {
  displayName: string;
  bundesland: string;
  team?: { name: string; slug: string; abbreviation?: string } | null;
  km: number;
  /** `null`, solange noch kein Kilometer eingetragen ist. */
  rank: number | null;
  /** Neueste zuerst. */
  rides: ChallengeRide[];
  /** Eigene Beitrittsanfragen, solange man in keinem Team ist; offene zuerst. */
  teamRequests?: ChallengeMyTeamRequest[];
  /** Offene Anfragen an das Team, das ich gegründet habe. */
  openRequestsToMyTeam?: number;
}

/** `PENDING`: wartet auf den Gründer. `DECLINED`: abgelehnt, dasselbe Team lässt sich nicht noch einmal anfragen. */
export type ChallengeTeamRequestStatus = 'PENDING' | 'DECLINED';

export interface ChallengeMyTeamRequest {
  team: { name: string; slug: string; abbreviation?: string };
  status: ChallengeTeamRequestStatus;
}

/** Offene Anfrage an ein Team, nur für den Gründer (`ChallengeTeam.requests`). */
export interface ChallengeTeamRequest {
  id: number;
  participantId: number;
  displayName: string;
  bundesland?: string | null;
  km: number;
  requestedAt: string;
}

export type ChallengeBoardKind = 'riders' | 'teams' | 'regions' | 'countries';

export interface ChallengeBoardRow {
  rank: number;
  /** Anzeigename, Teamname, Region (kanonischer Wert) bzw. Land (`CountryCode`). */
  name: string;
  km: number;
  /** Teams, Regionen, Länder: wie viele dabei sind. */
  members?: number;
  /** Einzel: Region des Fahrers, Team: verknüpfter Veranstalter. */
  detail?: string | null;
  /** Team mit verknüpftem Veranstalter (Haken). */
  verified?: boolean;
  isMe?: boolean;
  /** Nur Einzel: Kürzel des aktuellen Teams, vor dem Fahrernamen. */
  teamAbbreviation?: string | null;
  /** Einzel: Teilnehmer-Id, zum Melden. */
  id?: number | null;
  /** Teams: Adresse der Teamseite. */
  slug?: string | null;
}

export interface ChallengeBoard {
  kind: ChallengeBoardKind;
  rows: ChallengeBoardRow[];
  /** Die eigene Zeile, wenn sie nicht unter `rows` steht. */
  me?: ChallengeBoardRow | null;
  total: number;
}

/** Teamseite `/winterchallenge/team/:slug`. */
export interface ChallengeTeam {
  slug: string;
  name: string;
  abbreviation: string;
  description?: string | null;
  /**
   * Verknüpfter Veranstalter; nur ein Konto mit Zugang zu ihm kann das setzen. Die Teamseite zeigt
   * ihn mit seinen nächsten Events — der Grund, warum ein Verein hier mitmacht.
   */
  organizer?: ChallengeTeamOrganizer | null;
  /** Verknüpfung beantragt, der Zugang zum Veranstalter steht noch aus. Nur für Mitglieder. */
  organizerPending?: { id: number; name: string } | null;
  km: number;
  rank: number | null;
  /** Alle Teams der Saison, für „Platz 12 von 312“. */
  teamsTotal: number;
  /** Nur für Mitglieder: der Link, mit dem man andere einlädt. */
  inviteUrl?: string | null;
  /** Ich bin Mitglied. */
  isMember: boolean;
  /** Ich darf Name, Beschreibung und Verein ändern (Gründer). */
  canEdit: boolean;
  /** Nach der Nachtragsfrist sind auch erste Teambeitritte geschlossen. */
  joinClosed?: boolean;
  members: { participantId: number; displayName: string; bundesland: string; km: number; isMe?: boolean; friendshipState?: import('./community').FriendshipState | null }[];
  /** Meine Beitrittsanfrage an dieses Team, `null` ohne. */
  myRequest?: ChallengeTeamRequestStatus | null;
  /** Offene Anfragen; nur für den Gründer, sonst `null`. */
  requests?: ChallengeTeamRequest[] | null;
}

/** Ein Team in der Verwaltung (`GET /admin/challenge/teams`): umbenennen und ausblenden. */
export interface ChallengeAdminTeam {
  id: number;
  name: string;
  slug: string;
  abbreviation: string;
  hidden: boolean;
  members: number;
  km: number;
  organizerName?: string | null;
  /** Anzeigename des Gründers, `null` wenn das Konto weg ist. */
  createdBy?: string | null;
  createdAt: string;
}

export interface ChallengeTeamOrganizer {
  id: number;
  name: string;
  verified: boolean;
  city?: string | null;
  logoUrl?: string | null;
  /** Höchstens drei kommende Events des Veranstalters. */
  upcomingEvents: { id: number; title: string; eventDate: string; city?: string | null }[];
}

/** Treffer der Vereinssuche beim Team-Gründen. */
export interface ChallengeOrganizerHit {
  id: number;
  name: string;
  city?: string | null;
  verified: boolean;
  /** Mein Konto hat Zugang (aktive Verknüpfung): das Team fährt sofort für den Verein. */
  hasAccess: boolean;
  /** Der Verein hat schon ein Team; beitreten geht über dessen Einladungslink. */
  existingTeam?: { name: string; slug: string } | null;
}

/** Abschnitt Winterchallenge auf der Veranstalterseite (`GET /challenge/current/organizers/:id`). */
export interface OrganizerChallenge {
  summary: ChallengeSummary;
  team: { slug: string; name: string; km: number; rank: number | null; teamsTotal: number; memberCount: number } | null;
  /** Das angemeldete Konto verwaltet den Veranstalter. */
  canManage: boolean;
}

/** Ein Teilnehmer in der Verwaltung: Fair Play („auffällig“) und die Liste aller. */
export interface ChallengeFlag {
  participantId: number;
  userId: number;
  displayName: string;
  /** Konto-Adresse, nur für Admins; Moderatoren bekommen `null` (wie überall sonst). */
  email: string | null;
  bundesland: string;
  teamName?: string | null;
  km: number;
  rank: number | null;
  /** Kilometer der letzten 7 Tage. */
  kmLast7Days: number;
  /** Größte Tagessumme der Saison. */
  maxDayKm: number;
  rides: number;
  ridesWithoutProof: number;
  /** Wie oft andere den Teilnehmer gemeldet haben. */
  reports: number;
  /** Warum er in der Liste steht. */
  reasons: ('WEEK_KM' | 'DAY_AVERAGE' | 'REPORTED')[];
  hidden: boolean;
  /** Die auffälligsten Fahrten, größte zuerst; in der Liste aller leer. */
  topRides: ChallengeRide[];
  /** Mitmachen bestätigt (ISO-Zeitpunkt). */
  joinedAt?: string | null;
  /** Tag der letzten Fahrt, `null` ohne Fahrt. */
  lastRideOn?: string | null;
  /** Davon auf der Rolle. */
  kmIndoor?: number;
}
